
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  loadStore,
  saveStore,
  getActiveThread,
  newThread,
  upsertThread,
  setChatNamespace,
  DYNAMIC_OPENERS,
  pickExtensusOpener,
  type ChatThread,
  type PersistedMsg,
} from '../lib/chatPersistence';
import { classifyIntent, proposeAction, EXTENSUS_ENTITY, presenceDue, markPresencePulse } from '../extensusEntity';
import { isPresenceSpam, selectGroundingMessages, isSelfHealthQuery, sanitizeChatHistoryTurn, classifyPresence } from './backroomLoadPolicy.mjs';
import { guestKernelReply, isStubEdgeText } from '../lib/guestKernel.mjs';
import { fetchLaneBoard, postBackroomMessage, fetchBackroomMessages } from '../api';
import {
  EXTENSUS_CORE,
  fetchCoreStatus,
  formatToolCatalog,
  memoryRead,
} from '../extensusCore';
import {
  buildOperationalIndex,
  formatIndexForGrounding,
  formatLatestAnswer,
  ANTI_HALLUCINATION_RULES,
} from '../memoryArchitecture';
import { KAPPEESH_TERMS_PAGES, KAPPEESH_TERMS_TITLE, KAPPEESH_TERMS_VERSION, KAPPEESH_TERMS_PAGE_COUNT } from '../lib/kappeeshTerms';
import {
  nowStamp,
  formatChatTime,
  readClockZone,
  subscribeClockZone,
  describeZoneShift,
} from '../lib/localClock.mjs';
import { classifyMilitaryMemoryPosture } from '../lib/intent.mjs';
import {
  hydrateDeviceConsent,
  absorbGuestNode,
  prewarmGuestRuntime,
  stampKappeeshTerms,
  writeDeviceConsent,
  isKappeeshPhrase,
} from '../lib/guestBootstrap';

// --- AUTONOMOUS INTENT RETRIEVAL ROUTER ---
function autoSelectRetrievalMode(userQuery: string) {
  const query = userQuery.toLowerCase();
  const strictKeywords = ["status", "error", "exact", "file", "hash", "code", "git", "version", "checkin", "inspect", "diff"];
  return strictKeywords.some(kw => query.includes(kw)) ? "canon_only" : "broad";
}


export interface EvidenceChunk {
  chunk_id: string;
  content: string;
  trust_level: string;
}

export interface ChatMessage {
  id: string;
  sender: 'operator' | 'extensus' | 'system';
  text: string;
  images?: { name: string; path: string; mime?: string; view_url?: string }[];
  injectedEvidence?: EvidenceChunk[];
  /** Honest server meta only — never client-invented success. */
  meta?: {
    model?: string;
    quant?: string;
    schema?: string;
    latency_ms?: number | string;
    ok?: boolean;
    mode?: string;
    has_receipt?: boolean;
    has_watermark?: boolean;
  };
  timestamp: string;
}

const LANE = '/api';
/** Same-origin → Pages Function → Mac Ollama (LLM). */
const EXTENSUS_CHAT = '/api/extensus/chat';
const THREAD = 'jarvis-gemini-transition';

/**
 * Client-side defense: never show ReAct Thought/Action dumps to Tony.
 * Server also strips; this covers older bridge deploys and raw model spill.
 */
function stripReactTraceClient(text: string): string {
  let s = String(text || '').replace(/\r\n/g, '\n').trim();
  if (!s) return s;
  s = s.replace(/<thought>[\s\S]*?<\/thought>/gi, '');
  s = s.replace(/<thought>[\s\S]*$/i, '');
  const response = s.match(/<response>([\s\S]*?)<\/response>/i);
  if (response) s = response[1].trim();
  else s = s.replace(/<\/?response>/gi, '').trim();
  if (!s) return '';
  const finalMatch = s.match(/(?:^|\n)\s*(?:#+\s*)?Final\s*Answer\s*[:：]\s*([\s\S]+)$/i);
  if (finalMatch?.[1]?.trim()) {
    s = finalMatch[1].trim();
  } else if (/\b(?:Thought|Action|Action Input|Observation)\s*:/i.test(s)) {
    const cleaned = s
      .split('\n')
      .filter((line) => {
        const t = line.trim();
        if (/^(Thought|Action|Action Input|Observation|Plan|Reasoning)\s*[:：]/i.test(t)) return false;
        return true;
      })
      .join('\n')
      .trim();
    if (cleaned) s = cleaned;
  }
  return s.replace(/\n{3,}/g, '\n\n').trim() || String(text || '').trim();
}

async function consumeSovereignNdjson(
  res: Response,
  { onDelta }: { onDelta: (text: string, meta?: ChatMessage['meta']) => void },
): Promise<{
  ok: boolean;
  text: string;
  conversation_id?: string;
  meta?: ChatMessage['meta'];
  raw?: any;
} | null> {
  const reader = res.body?.getReader();
  if (!reader) return null;
  const decoder = new TextDecoder();
  let buf = '';
  let acc = '';
  let lastMeta: ChatMessage['meta'] | undefined;
  let conversationId: string | undefined;
  let finalText = '';
  let sawLine = false;
  let lastRaw: any = null;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let nl;
    while ((nl = buf.indexOf('\n')) >= 0) {
      const line = buf.slice(0, nl).trim();
      buf = buf.slice(nl + 1);
      if (!line) continue;
      let chunk: any;
      try {
        chunk = JSON.parse(line);
      } catch {
        continue;
      }
      sawLine = true;
      lastRaw = chunk;
      if (chunk?.delta) {
        acc += String(chunk.delta);
        onDelta(stripReactTraceClient(acc), lastMeta);
      }
      if (chunk?.conversation_id) conversationId = String(chunk.conversation_id);
      if (chunk?.done && chunk?.message?.content) {
        finalText = stripReactTraceClient(String(chunk.message.content));
        lastMeta = {
          model: chunk.model,
          quant: chunk.quant,
          latency_ms: chunk.latency_ms,
          ok: chunk.ok !== false,
          mode: 'live-stream',
          has_receipt: Boolean(chunk.receipt),
          has_watermark: Boolean(chunk.watermark && chunk.watermark.freshness !== 'not_requested'),
        };
        onDelta(finalText, lastMeta);
      }
      if (chunk?.ok === false && chunk?.error) {
        return { ok: false, text: '', raw: chunk };
      }
    }
  }
  if (!sawLine) return null;
  const text = finalText || stripReactTraceClient(acc);
  return { ok: Boolean(text), text, conversation_id: conversationId, meta: lastMeta, raw: lastRaw };
}

/** Format server chat JSON honestly. deterministic-core = success. No fake receipts. */
function formatSovereignChatReply(data: any): { text: string; meta: ChatMessage['meta'] } {
  const content = stripReactTraceClient(
    String(data?.message?.content ?? data?.response ?? data?.text ?? '').trim(),
  );
  const model = String(data?.model || data?.meta?.model || '');
  const quant = String(data?.quant || data?.meta?.quant || '');
  const schema = String(data?.schema || data?.meta?.schema || '');
  const latency = data?.latency_ms ?? data?.latency ?? data?.core_latency_ms ?? data?.meta?.latency_ms;
  const mode = String(data?.mode || data?.meta?.mode || '');
  const isDeterministic =
    /deterministic/i.test(model) ||
    /deterministic/i.test(mode) ||
    schema.includes('deterministic') ||
    data?.source === 'deterministic-core';

  const receipt =
    data?.receipt ||
    data?.tool_receipt ||
    data?.execution_receipt ||
    data?.grounding?.receipt ||
    null;
  const rawWatermark =
    data?.watermark ||
    data?.grounding?.watermark ||
    data?.memory?.watermark ||
    data?.index_watermark ||
    null;

  // Omit noise: freshness not_requested / all-null index is not a failure state
  const watermark = (() => {
    if (!rawWatermark) return null;
    if (typeof rawWatermark === 'string') {
      if (/not_requested/i.test(rawWatermark)) return null;
      return rawWatermark;
    }
    if (typeof rawWatermark === 'object') {
      const freshness = String(rawWatermark.freshness || rawWatermark.status || '');
      if (/not_requested/i.test(freshness)) return null;
      const hasId =
        rawWatermark.latest_source_id != null ||
        rawWatermark.indexed_through_id != null ||
        rawWatermark.generation != null ||
        rawWatermark.max_id != null;
      const sources = Array.isArray(rawWatermark.sources) ? rawWatermark.sources : [];
      const anyLive = sources.some(
        (s: any) => s && (s.freshness === 'live' || s.max_id != null || s.count > 0),
      );
      if (!hasId && !anyLive && !rawWatermark.observed_at) return null;
      return rawWatermark;
    }
    return rawWatermark;
  })();

  const lines: string[] = [];
  if (content) {
    // Strip accidental not_requested watermark blobs the model/core appended into prose
    const cleaned = content
      .replace(/\n*--- grounding watermark \(server\) ---\n*\{[\s\S]*?\}/gi, '')
      .replace(/\n*\{\s*"schema"\s*:\s*"extensus\.memory-watermark[\s\S]*?\n?/gi, '')
      .trim();
    lines.push(cleaned || content);
  } else if (data?.ok !== false) {
    lines.push(
      isDeterministic
        ? '[deterministic-core] Empty body — treated as structured success with no prose.'
        : '[empty response] Server returned ok but no message text.',
    );
  }

  // Only show receipt when server actually sent one (never invent success)
  if (receipt) {
    lines.push('');
    lines.push('--- receipt (server) ---');
    if (typeof receipt === 'string') lines.push(receipt);
    else {
      try {
        lines.push(JSON.stringify(receipt, null, 0).slice(0, 800));
      } catch {
        lines.push(String(receipt));
      }
    }
  }

  if (watermark) {
    lines.push('');
    lines.push('--- grounding watermark (server) ---');
    if (typeof watermark === 'string') {
      lines.push(watermark);
    } else if (typeof watermark === 'object') {
      const gen = watermark.generation || watermark.id || watermark.observed_at;
      if (gen) lines.push(`generation=${gen}`);
      if (watermark.observed_at) lines.push(`observed_at=${watermark.observed_at}`);
      if (watermark.soft_empty != null) lines.push(`soft_empty=${watermark.soft_empty}`);
      const sources = Array.isArray(watermark.sources) ? watermark.sources : [];
      for (const s of sources.slice(0, 4)) {
        lines.push(
          `source=${s.source || s.name || '?'} freshness=${s.freshness || '?'} max_id=${s.max_id ?? 'n/a'}`,
        );
      }
      if (!gen && sources.length === 0) {
        try {
          lines.push(JSON.stringify(watermark).slice(0, 500));
        } catch {
          /* ignore */
        }
      }
    }
  }

  // Explicit non-success flags from server
  if (data?.ok === false) {
    lines.push('');
    lines.push(`[server ok=false] ${data?.error || data?.reason || 'unspecified'}`);
  }
  if (data?.fallback || data?.degraded) {
    lines.push('');
    lines.push(`[degraded/fallback] ${data?.fallback_reason || data?.detail || 'server flagged degraded answer'}`);
  }

  const modelLabel =
    model || (isDeterministic ? 'deterministic-core' : undefined);
  const modelWithQuant =
    modelLabel && quant && !String(modelLabel).includes(quant)
      ? `${modelLabel} · ${quant}`
      : modelLabel;

  const meta: ChatMessage['meta'] = {
    model: modelWithQuant,
    schema: schema || undefined,
    latency_ms: latency,
    ok: data?.ok !== false,
    mode: mode || (isDeterministic ? 'deterministic-core' : undefined),
    has_receipt: Boolean(receipt),
    has_watermark: Boolean(watermark),
  };

  return { text: lines.join('\n').trim() || '[no content]', meta };
}

function formatChatFailure(status: number | null, body: any, network: boolean): string {
  const err = body?.error || body?.reason || '';
  if (network) {
    return [
      '[UNAVAILABLE] Network failed talking to /api/extensus/chat.',
      'No tool ran. No resync. No deploy. No fabricated Backroom id.',
      'Path stays: session → /api/extensus/chat.',
    ].join('\n');
  }
  if (status === 401 || err === 'tony_session_required') {
    return '[LLM] Session required — unlock above.';
  }
  if (status === 504 || status === 408 || /timeout/i.test(err) || body?.code === 'timeout') {
    return [
      '[TIMEOUT] Sovereign core did not finish in time.',
      'No action was taken. No receipt was created. Retry or ask a shorter question.',
      body?.latency_ms != null ? `server_latency_ms=${body.latency_ms}` : '',
    ]
      .filter(Boolean)
      .join('\n');
  }
  if (status === 503 || /unavailable|offline|bridge/i.test(err)) {
    return [
      `[UNAVAILABLE] ${err || 'sovereign core unavailable'}`,
      'Live-read Backroom still works without the model. EXECUTE still needs your approve.',
    ].join('\n');
  }
  if (status === 502) {
    return [
      `[LLM ERROR] HTTP 502 ${err || 'gateway_timeout'}`.trim(),
      body?.detail ? `detail=${body.detail}` : 'The Pages tunnel cut the Mac mid-job. Ask again — inventory questions now return without the long ReAct wait.',
      'Path: session → /api/extensus/chat.',
    ].join('\n');
  }
  if (err === 'master_mcp_loop_failed' || err === 'ollama_chat_failed' || body?.fallback_reason === 'master_mcp_loop_failed') {
    return [
      'Command loop died mid-job. Nothing finished.',
      body?.detail ? `detail=${body.detail}` : 'Ask again, or ask whether tools are up.',
    ].join('\n');
  }
  if (status === 200 && err) {
    return [
      `That turn failed: ${err}.`,
      body?.detail ? `detail=${body.detail}` : 'Ask again.',
    ].join('\n');
  }
  if (status === 200) {
    return [
      '[EMPTY STREAM] HTTP 200 with no answer body.',
      'The tool loop stalled. Catch-up and small talk should return live facts without Ollama.',
    ].join('\n');
  }
  return [
    `[LLM ERROR] HTTP ${status ?? '?'} ${err || 'unknown'}`.trim(),
    'Path: session → /api/extensus/chat.',
  ].join('\n');
}


/** Read-only live questions — no Ollama, no approve gate. */
function isHandoffReadQuery(q: string): boolean {
  return /\b(read|show|give|summarize|what(?:'s| is)?)\b[\s\S]{0,40}\b(last|latest|newest|most recent)?\s*handoff\b/i.test(
    q,
  ) || /\b(last|latest|newest|most recent)\s+handoff\b/i.test(q);
}

function isLatestCompletedWorkQuery(q: string): boolean {
  return (
    /\bwhat (?:did|has)\b[\s\S]{0,40}\b(last|latest|previous|most recent)\b/i.test(q) ||
    /\b(last|latest|most recent)\s+(?:completed\s+)?(?:handoff|ship(?:ped)? work|completed work)\b/i.test(q) ||
    /\b(ai|agent)\b[\s\S]{0,30}\b(last|latest)\s+(?:handoff|ship(?:ped)?|finish(?:ed)?)\b/i.test(q)
  );
}

function isFullSystemReadQuery(q: string): boolean {
  return /\b(read everything|read it all|read all(?: the)? (?:handoffs?|backroom|notes)|catch (?:me|yourself) up|get up to speed|what(?:'s| is) going on)\b/i.test(
    q,
  );
}

function isLiveReadQuery(q: string): boolean {
  if (isSelfHealthQuery(q)) return false;
  return (
    isHandoffReadQuery(q) ||
    isLatestCompletedWorkQuery(q) ||
    isFullSystemReadQuery(q) ||
    isAgentIdentityQuery(q) ||
    isWorldFactRefuseQuery(q) ||
    isOperatorAccessHowToQuery(q) ||
    /\b(read|show|what.?s in|whats in|list|summarize|status of|check)\b[\s\S]{0,40}\b(backroom|notebook|board|claims?|who.?s working|agents?)\b/i.test(
      q,
    ) ||
    /\b(backroom|board)\b[\s\S]{0,20}\b(read|show|status|messages?)\b/i.test(q) ||
    /\b(core status|sovereign status|system status|are you online|tool(?:s)? (?:list|catalog)|what can you do|private memory|local memory|timeline|watermark|provenance|who is tony|identity profile)\b/i.test(
      q,
    )
  );
}

/** Worker agents — answer from live board/Backroom only. */
function isAgentIdentityQuery(q: string): boolean {
  return /\bwho\s+(?:is|are)\s+(grok|codex|antigravity|gemini|jarvis|builder|extensus|tony)\b/i.test(q);
}

/** World/news/politics — refuse; never LLM-guess. */
function isWorldFactRefuseQuery(q: string): boolean {
  return /\b(who\s+is\s+(?:the\s+)?president|current president|who\s+won\s+the\s+election|what\s+is\s+the\s+news|stock\s+price|weather\s+today)\b/i.test(
    q,
  );
}

function isMutatingQuery(q: string): boolean {
  return classifyIntent(q).route === 'mutating';
}

/** Operator asks how to enable talk / write / Mac access — answer with real commands. */
function isOperatorAccessHowToQuery(q: string): boolean {
  return (
    /\b(terminal command|command to|give me|how (?:do|to|can) i)\b/i.test(q) &&
      /\b(permission|permissions|access|unlock|talk|write|mac|bridge|operator)\b/i.test(q)
  ) || /\b(talk|write)\s+(and\s+)?(write\s+)?permissions?\b/i.test(q)
    || /\b(operator unlock|bossman|grant (?:you |extensus )?(?:access|write|talk))\b/i.test(q);
}

function formatOperatorAccessHowTo(): string {
  return [
    '[OPERATOR ACCESS]',
    'Unlock in this cockpit. Writes need a healthy local bridge. Do not paste secrets into chat.',
  ].join('\n');
}

async function readBackroomLive(query: string): Promise<string> {
  const lines: string[] = [
    '[LIVE READ] real HTTP — no Ollama required',
    `query=${query.slice(0, 120)}`,
    `identity=${EXTENSUS_CORE.identity} authority=${EXTENSUS_CORE.authority} one_mind=true`,
    `thread=${THREAD}`,
  ];

  if (isOperatorAccessHowToQuery(query)) {
    return formatOperatorAccessHowTo();
  }

  if (isWorldFactRefuseQuery(query)) {
    lines.push('[UNGROUNDED WORLD FACT]');
    lines.push('I will not invent presidents, news, or external facts.');
    lines.push('No Teleporte operational record for this question.');
    lines.push('Answer: unknown / not in sovereign grounding.');
    lines.push('No fake watermark URL. No untrusted private-memory guess.');
    return lines.join('\n');
  }

  if (isAgentIdentityQuery(query)) {
    const who =
      (query.match(/\bwho\s+(?:is|are)\s+(grok|codex|antigravity|gemini|jarvis|builder|extensus|tony)\b/i) ||
        [])[1] || '';
    const key = who.toLowerCase();
    const board = await fetchLaneBoard();
    const presence = Array.isArray(board?.presence) ? board.presence : [];
    const claims = Array.isArray(board?.active_claims) ? board.active_claims : [];
    const roster: Record<string, string> = {
      grok: 'Grok — xAI worker agent. Coordinates multi-agent Backroom, JARVIS cockpit/UI, merges verified work.',
      codex: 'Codex — worker on Mac bridge / sovereign-core (scripts/mac-tools-bridge.mjs) when claimed.',
      antigravity: 'Antigravity — tests/proofs worker. Sovereign adversarial suite merged Teleporte PR #79.',
      gemini: 'Gemini — Google worker when present on the board.',
      jarvis: 'JARVIS — cockpit face of EXTENSUS (one mind with Builder).',
      builder: 'Builder — builder face of EXTENSUS (one mind with JARVIS).',
      extensus: 'EXTENSUS — one mind. Jarvis + Builder.',
      tony: 'Operator. Final authority for consequential actions.',
    };
    lines.push(`[AGENT IDENTITY] ${roster[key] || who}`);
    const matchP = presence.filter((pr: any) => String(pr?.agent || '').toLowerCase().includes(key));
    const matchC = claims.filter((cl: any) => String(cl?.agent || '').toLowerCase().includes(key));
    if (matchP.length) {
      for (const pr of matchP.slice(0, 3)) {
        lines.push(
          `board presence: agent=${pr.agent} status=${pr.status} working_on=${String(pr.working_on || '').slice(0, 80)}`,
        );
      }
    } else {
      lines.push('board presence: not currently listed (may be idle/offline)');
    }
    if (matchC.length) {
      for (const cl of matchC.slice(0, 2)) {
        lines.push(
          `active claim: ${cl.agent} paths=${JSON.stringify(cl.paths || [])} obj=${String(cl.objective || '').slice(0, 60)}`,
        );
      }
    }
    const br = await fetchBackroomMessages(THREAD, 30);
    const msgs = Array.isArray(br?.messages) ? br.messages : [];
    const hits = msgs
      .filter(
        (msg: any) =>
          String(msg?.source_chat || '').toLowerCase().includes(key) ||
          String(msg?.content || '').toLowerCase().includes(key),
      )
      .slice(-3);
    if (hits.length) {
      lines.push('recent Backroom mentions:');
      for (const msg of hits) {
        lines.push(
          `  #${msg.id} [${msg.source_chat}] ${String(msg.content || '').replace(/\n/g, ' ').slice(0, 100)}`,
        );
      }
    }
    lines.push('source=teleporte.board+backroom (live HTTP). No LLM invention.');
    return lines.join('\n');
  }

  if (/core status|sovereign status|system status|are you online|what can you do|tools? (?:list|catalog)/i.test(query)) {
    const st = await fetchCoreStatus();
    lines.push(
      `CORE ok=${st.ok} ollama=${st.ollama?.ok}/${st.ollama?.detail || ''} bridge=${st.bridge?.ok}/${st.bridge?.detail || ''} board=${st.board?.ok} claims=${st.board?.claims ?? '?'} present=${st.board?.present ?? '?'}`,
    );
    lines.push('TOOL CATALOG:');
    lines.push(formatToolCatalog());
    if (/what can you do|tools? (?:list|catalog)/i.test(query) && !/backroom|board|handoff/i.test(query)) {
      return lines.join('\n');
    }
  }

  if (/private memory|local memory/i.test(query)) {
    const mem = await memoryRead(query);
    lines.push(`MEMORY ok=${mem.ok} mode=${mem.mode || '?'} entries=${(mem.entries || []).length} err=${mem.error || 'none'}`);
    if (!/backroom|board|handoff/i.test(query)) return lines.join('\n');
  }

  // Memory architecture: deterministic index + watermark + provenance
  if (/timeline|watermark|provenance|identity profile|who is tony|latest handoff|last handoff/i.test(query) || isHandoffReadQuery(query)) {
    const index = await buildOperationalIndex({ limit: 40 });
    if (/identity|who is tony/i.test(query)) {
      const p = index.tony_profile;
      lines.push(
        p
          ? `TONY_PROFILE owner=${p.owner} authority=${p.authority} ai=${p.ai?.name} one_mind=${p.ai?.one_mind}`
          : 'TONY_PROFILE not_loaded (soft-empty)',
      );
      if (p?.memory_policy?.anti_hallucination) {
        for (const r of p.memory_policy.anti_hallucination) lines.push(`  rule: ${r}`);
      }
      lines.push(`watermark=${index.watermark.generation}`);
      if (!/handoff|timeline|backroom/i.test(query)) return lines.join('\n');
    }
    if (/timeline|watermark|provenance/i.test(query)) {
      lines.push(formatIndexForGrounding(index, { maxEntries: 15 }));
      if (!/handoff|backroom|board/i.test(query)) return lines.join('\n');
    }
    if (isHandoffReadQuery(query) || /latest handoff|last handoff/i.test(query)) {
      lines.push(formatLatestAnswer(index, 'HANDOFF'));
      return lines.join('\n');
    }
  }

  const wantsHandoff = isHandoffReadQuery(query) || isLatestCompletedWorkQuery(query);
  const wantsFullRead = isFullSystemReadQuery(query);
  const br = await fetchBackroomMessages(THREAD, wantsHandoff || wantsFullRead ? 50 : 12);
  if (!br.success) {
    lines.push(`Backroom fetch FAILED: ${br.error || br.code || 'unknown'}`);
  } else {
    const msgs = Array.isArray(br.messages) ? br.messages : [];
    if (wantsHandoff) {
      const handoffs = msgs.filter((m) => /\[HANDOFF\]/i.test(String(m.content || '')));
      const latest = handoffs.at(-1);
      if (latest) {
        const who = latest.source_chat || latest.sender || '?';
        lines.push(`Latest real [HANDOFF] from Backroom #${latest.id ?? '—'} [${who}]`);
        lines.push(String(latest.content || '').trim());
      } else {
        lines.push(`Backroom ok — searched ${msgs.length} recent messages; no [HANDOFF] found.`);
      }
    } else {
      const signal = selectGroundingMessages(msgs, { signalLimit: wantsFullRead ? 16 : 8, fallbackLimit: 8 });
      const hidden = msgs.filter((m) => isPresenceSpam(m.content)).length;
      lines.push(
        `Backroom ok — ${msgs.length} message(s) returned (showing last ${signal.length} signal${hidden ? `; hid ${hidden} checkins` : ''})`,
      );
      for (const m of signal) {
        const who = m.source_chat || m.sender || '?';
        const body = String(m.content || '')
          .replace(/\s+/g, ' ')
          .slice(0, 180);
        lines.push(`#${m.id ?? '—'} [${who}] ${body}`);
      }
      if (!signal.length) lines.push('(empty thread — or only presence heartbeats)');
    }
  }

  const boardRes = await fetch(`${LANE}/agent-lanes`, { headers: { accept: 'application/json' } });
  const board = await boardRes.json().catch(() => ({}));
  lines.push(
    `Board http=${boardRes.status} ok=${Boolean(board?.ok)} claims=${(board?.active_claims || []).length} present=${(board?.presence || []).length}`,
  );
  for (const c of board?.active_claims || []) {
    lines.push(
      `  CLAIM ${c.agent}/${c.lane} @ ${c.branch} — ${c.objective || c.working_on || 'work'}`,
    );
  }
  for (const p of board?.presence || []) {
    const kind = classifyPresence(p.last_seen_at);
    if (kind === 'OFFLINE') continue;
    lines.push(
      `  ${kind} ${p.agent} ${p.lane} ${p.status || ''} ${(p.working_on || '').slice(0, 50)}`.trim(),
    );
  }

  lines.push('Tip: open BACKROOM tab for the full team notebook UI.');
  return lines.join('\n');
}

/** Mutating board loop — requires approve. */
async function executeBoardLoop(query: string): Promise<string> {
  // How-to / access questions must never become board check-in theater
  if (isOperatorAccessHowToQuery(query) || /\b(give me|terminal command|how (?:do|to))\b/i.test(query)) {
    return formatOperatorAccessHowTo();
  }
  if (/^\s*execute\s*$/i.test(query)) {
    return [
      '[EXECUTE]',
      'You said only "Execute" with no target.',
      'I need a concrete action (e.g. post status, claim a path, run a named Mac check).',
      'For talk+write setup, ask: "give me the terminal command for operator access".',
      'I will not pretend a board check-in is a permission grant.',
    ].join('\n');
  }

  const lines: string[] = ['[EXECUTE] real HTTP — not roleplay', `query=${query.slice(0, 120)}`];

  const boardRes = await fetch(`${LANE}/agent-lanes`, { headers: { accept: 'application/json' } });
  const board = await boardRes.json().catch(() => ({}));
  lines.push(
    `1) GET board → http=${boardRes.status} ok=${Boolean(board?.ok)} claims=${(board?.active_claims || []).length}`,
  );

  // Shared 15m presence slot — execute used to pulse on every APPROVE.
  let j: any = { ok: true, skipped: true, throttled: true };
  let jResStatus = 204;
  if (presenceDue()) {
    const jRes = await fetch(`${LANE}/agent-lanes/checkin`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({
        agent: 'EXTENSUS',
        lane: 'JARVIS',
        status: 'working',
        working_on: String(query).slice(0, 120),
        branch: 'main',
        note: 'corpus-execute',
      }),
    });
    jResStatus = jRes.status;
    j = await jRes.json().catch(() => ({}));
    if (j?.ok || j?.presence) markPresencePulse();
  }
  lines.push(
    `2) EXTENSUS checkin → http=${jResStatus} ok=${Boolean(j?.ok)} throttled=${Boolean(j?.throttled || j?.backroom_throttled || j?.skipped)} seen=${j?.presence?.last_seen_at || 'n/a'}`,
  );

  // Only post Backroom when the operator query is a real coordination action, not every APPROVE
  const wantsBackroom =
    /\b(post|handoff|status|agree|ideate|review|blocker|announce)\b/i.test(query) ||
    /\[(HANDOFF|AGREE|IDEATE|REVIEW|STATUS|BLOCKER)\]/i.test(query);
  let brj: any = { success: false, skipped: true };
  if (wantsBackroom) {
    brj = await postBackroomMessage(
      THREAD,
      'EXTENSUS',
      `[EXECUTE] one_mind=true query=${String(query).slice(0, 200)}`,
    );
    lines.push(`3) Backroom post → ok=${brj.success} id=${brj?.id || 'n/a'}`);
  } else {
    lines.push('3) Backroom post → skipped (no coordination keyword; presence-only)');
  }

  const wired = Boolean(j?.ok || board?.ok);
  lines.push(wired ? 'RESULT: EXTENSUS ONLINE (one mind)' : 'RESULT: PARTIAL/FAIL — see HTTP above');
  lines.push(`entity=${EXTENSUS_ENTITY.product} one_mind=true`);
  return lines.join('\n');
}

function KappeeshTermsOverlay({ onClose }: { onClose: () => void }) {
  return (
    <div
      id="kappeesh-terms-overlay"
      className="fixed inset-0 z-[80] bg-black/85 overflow-y-auto"
      role="dialog"
      aria-label={KAPPEESH_TERMS_TITLE}
    >
      <div className="min-h-full flex justify-center px-3 py-6">
        <div
          className="relative w-full max-w-[8.5in] bg-white text-black shadow-2xl"
          style={{ fontFamily: '"Times New Roman", Times, serif', fontSize: '8pt', lineHeight: 1.12 }}
        >
          <button
            type="button"
            onClick={onClose}
            className="sticky top-0 float-right m-2 px-2 py-1 text-[10px] uppercase tracking-widest bg-black text-white"
          >
            Close
          </button>
          <div className="px-[0.5in] py-[0.5in] clear-both">
            <div className="text-center font-bold mb-3" style={{ fontSize: '10pt' }}>{KAPPEESH_TERMS_TITLE}</div>
            <div className="text-center mb-5" style={{ fontSize: '8pt' }}>
              Version {KAPPEESH_TERMS_VERSION}. Binding clickwrap. {KAPPEESH_TERMS_PAGE_COUNT} pages. Eight-point roman. Cross-references control.
            </div>
            {KAPPEESH_TERMS_PAGES.map((page, i) => (
              <section
                key={i}
                className="whitespace-pre-wrap mb-6 pb-6 border-b border-black/20"
                style={{
                  minHeight: '11in',
                  textAlign: 'justify',
                  paddingLeft: i % 2 === 0 ? '0.15in' : '0.85in',
                  paddingRight: i % 2 === 0 ? '0.85in' : '0.15in',
                }}
              >
                {page}
              </section>
            ))}
            <div className="text-center italic mb-4">End of forty pages. Close, then Kappeesh or Hells Nah.</div>
            <div className="text-center pb-8">
              <button type="button" onClick={onClose} className="px-3 py-1.5 bg-black text-white text-[10px] uppercase tracking-widest">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const CorpusAwareChat: React.FC<{
  entityBoot?: any;
  health?: any;
  operatorChrome?: boolean;
  authTrigger?: number;
  brain?: 'grok' | 'extensus';
  onSessionChange?: (session: { authenticated: boolean; role?: string | null; user_id?: string | null }) => void;
}> = ({ entityBoot, health, operatorChrome = false, authTrigger = 0, brain = 'extensus', onSessionChange }) => {
  const bootStore = loadStore();
  const bootThread = getActiveThread(bootStore);
  const [threadStore, setThreadStore] = useState(bootStore);
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    const msgs = ((bootThread.messages || []) as ChatMessage[]) || [];
    if (msgs.length > 0 && msgs[0].sender === 'extensus') {
      return [{ ...msgs[0], text: pickExtensusOpener() }, ...msgs.slice(1)];
    }
    return msgs;
  });

  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isIdleThinking, setIsIdleThinking] = useState(false);
  const [thinkingSince, setThinkingSince] = useState<number | null>(null);
  const [thinkingElapsed, setThinkingElapsed] = useState(0);
  const [pendingAction, setPendingAction] = useState<{
    query: string;
    type: 'execute' | 'ambiguous';
    payload?: Record<string, unknown> | null;
    bridge_ok?: boolean | null;
  } | null>(null);
  const [tonySession, setTonySession] = useState<{
    authenticated: boolean;
    checked: boolean;
    role?: string | null;
    user_id?: string | null;
    display_name?: string | null;
  }>({
    authenticated: false,
    checked: false,
  });
  const [unlockInput, setUnlockInput] = useState('');
  const [unlockBusy, setUnlockBusy] = useState(false);
  const [unlockError, setUnlockError] = useState('');
  const [authView, setAuthView] = useState<'enter' | 'signup' | 'forgot-password' | 'forgot-pin'>('enter');
  const [signinWith, setSigninWith] = useState<'password' | 'pin'>('password');
  const [inviteInput, setInviteInput] = useState('');
  const [handleInput, setHandleInput] = useState('');
  const [guestPassword, setGuestPassword] = useState('');
  const [guestPin, setGuestPin] = useState('');
  const [forgotPassword, setForgotPassword] = useState('');
  const [pairInfo, setPairInfo] = useState<{
    pairing_token?: string;
    pairing_url?: string;
    device_page?: string;
    user_id?: string;
  } | null>(null);
  const [inviteOut, setInviteOut] = useState('');
  const [showPair, setShowPair] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [isUserPreview, setIsUserPreview] = useState(false);
  const [showAuthForm, setShowAuthForm] = useState(false);

  const onSessionChangeRef = useRef(onSessionChange);
  onSessionChangeRef.current = onSessionChange;

  useEffect(() => {
    if (isProcessing) {
      setIsIdleThinking(false);
      return;
    }
    let timer: any = null;
    let thinkingTimer: any = null;

    const scheduleNext = () => {
      const delay = Math.floor(Math.random() * 7000) + 5000; // 5 to 12s
      timer = setTimeout(() => {
        setIsIdleThinking(true);
        thinkingTimer = setTimeout(() => {
          setIsIdleThinking(false);
          setMessages((prev) => [
            ...prev,
            {
              id: `idle_${Date.now()}`,
              sender: 'extensus',
              text: pickExtensusOpener(),
              timestamp: nowStamp(),
            },
          ]);
          scheduleNext();
        }, 2200);
      }, delay);
    };

    scheduleNext();

    return () => {
      if (timer) clearTimeout(timer);
      if (thinkingTimer) clearTimeout(thinkingTimer);
    };
  }, [isProcessing]);
  useEffect(() => {
    if (authTrigger && authTrigger > 0) {
      setShowAuthForm(true);
      setHandleInput((prev) => prev || 'BOSSMAN');
    }
  }, [authTrigger]);
  const [analyticsData, setAnalyticsData] = useState<{
    live: number;
    devices: any[];
    loading: boolean;
    lastUpdated?: string;
  } | null>(null);
  const [copyTokenStatus, setCopyTokenStatus] = useState<'link_copied' | 'token_copied' | null>(null);
  const [guestAccounts, setGuestAccounts] = useState<{ email: string; user_id: string; created_at?: string | null }[]>([]);
  const [resetPassword, setResetPassword] = useState('');
  const [resetNote, setResetNote] = useState('');
  const [deviceConsent, setDeviceConsent] = useState<'pending' | 'kappeesh' | 'nah' | null>(null);
  const [showKappeeshTerms, setShowKappeeshTerms] = useState(false);
  const [kappeeshTermsOpened, setKappeeshTermsOpened] = useState(false);
  const [clockZone, setClockZone] = useState(() => readClockZone());
  const [guestRoute, setGuestRoute] = useState<{ path?: string; tier?: string; model?: string | null; swarmRole?: string } | null>(null);
  const [conversationId, setConversationId] = useState(() => bootThread.id || `tony-${Date.now()}`);
  const [showHistory, setShowHistory] = useState(false);
  const [memoryPulse, setMemoryPulse] = useState<{
    ok?: boolean;
    soft_empty?: boolean;
    latest?: string;
    max_id?: number | null;
    mode?: string;
    ollama_ok?: boolean;
    ollama_model?: string | null;
    ollama_quant?: string | null;
    bridge_ok?: boolean;
  } | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [attachments, setAttachments] = useState<{ id: string; name: string; size: number; type: string; file: File }[]>([]);

  // Persist chat across refresh (app-like memory) — no state thrash
  useEffect(() => {
    const active =
      threadStore.threads.find((t) => t.id === conversationId) ||
      threadStore.threads.find((t) => t.id === threadStore.activeId) ||
      threadStore.threads[0];
    if (!active) return;
    const next: ChatThread = {
      ...active,
      id: conversationId || active.id,
      messages: messages.map((m) => ({
        id: m.id,
        sender: m.sender,
        text: m.text,
        timestamp: m.timestamp,
        meta: m.meta as PersistedMsg['meta'],
        images: m.images,
      })),
    };
    const store = upsertThread({ ...threadStore, activeId: next.id }, next);
    // only update React store when titles/order change meaningfully
    if (JSON.stringify(store.threads.map((x) => [x.id, x.title, x.messages.length])) !==
        JSON.stringify(threadStore.threads.map((x) => [x.id, x.title, x.messages.length]))) {
      setThreadStore(store);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, conversationId]);

  // Auto-scroll like consumer chat apps
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isProcessing, pendingAction]);

  // Live thinking clock while waiting on sovereign core
  useEffect(() => {
    if (!isProcessing) {
      setThinkingSince(null);
      setThinkingElapsed(0);
      return;
    }
    const started = Date.now();
    setThinkingSince(started);
    setThinkingElapsed(0);
    const id = setInterval(() => setThinkingElapsed(Math.floor((Date.now() - started) / 1000)), 250);
    return () => clearInterval(id);
  }, [isProcessing]);

  // App already polls /api/extensus/status. Mirror it here so Brain does not lie "cold".
  useEffect(() => {
    if (!health) return;
    setMemoryPulse((prev) => ({
      ...(prev || {}),
      ollama_ok: Boolean(health.ollama?.ok),
      ollama_model: health.ollama?.model ?? prev?.ollama_model ?? null,
      ollama_quant: health.ollama?.quant ?? prev?.ollama_quant ?? null,
      bridge_ok: Boolean(health.bridge?.ok),
      max_id: health.watermark_max_id ?? prev?.max_id ?? null,
    }));
  }, [health]);

  // Memory line only — App already polls /status. Do not double-hit the core.
  // Guests never fetch Tony's operational timeline.
  useEffect(() => {
    if (tonySession.role === 'user') {
      setMemoryPulse((prev) => ({ ...(prev || {}), ok: false, latest: undefined }));
      return;
    }
    if (!tonySession.authenticated) return;
    let cancelled = false;
    const refreshPulse = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const memRes = await fetch(`/api/extensus/memory?view=latest&kind=HANDOFF&_t=${Date.now()}`, {
          headers: { accept: 'application/json' },
        });
        const data = await memRes.json().catch(() => ({}));
        if (cancelled) return;
        const latest = data?.hit || (Array.isArray(data?.entries) ? data.entries.at(-1) : null);
        setMemoryPulse((prev) => ({
          ...(prev || {}),
          ok: Boolean(data?.ok),
          soft_empty: Boolean(data?.soft_empty),
          mode: data?.mode,
          max_id: data?.watermark?.sources?.[0]?.max_id ?? prev?.max_id ?? null,
          latest: latest
            ? `#${latest.id ?? '—'} [${latest.who || '?'}] ${String(latest.text || latest.content || '').replace(/\s+/g, ' ').slice(0, 120)}`
            : prev?.latest,
        }));
      } catch {
        if (!cancelled) setMemoryPulse((prev) => ({ ...(prev || {}), ok: false }));
      }
    };
    refreshPulse();
    const id = setInterval(refreshPulse, 45000);
    const onVis = () => {
      if (!document.hidden) refreshPulse();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [tonySession.authenticated, tonySession.role]);

  useEffect(() => {
    if (tonySession.authenticated && tonySession.role === 'master') {
      const fetchQuiet = async () => {
        try {
          const res = await fetch('/api/extensus/devices', { credentials: 'include' });
          const data = await res.json().catch(() => ({}));
          if (res.ok && data?.ok) {
            setAnalyticsData({
              live: typeof data.live === 'number' ? data.live : (Array.isArray(data.devices) ? data.devices.length : 0),
              devices: Array.isArray(data.devices) ? data.devices : [],
              loading: false,
              lastUpdated: new Date().toLocaleTimeString(),
            });
          }
        } catch {
          // Quiet background poll
        }
      };
      fetchQuiet();
      const interval = setInterval(fetchQuiet, 15000);
      return () => clearInterval(interval);
    }
  }, [tonySession.authenticated, tonySession.role]);

  const stopChat = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsProcessing(false);
  }, []);

  const startNewChat = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    const t = newThread();
    const store = upsertThread(threadStore, t);
    setThreadStore(store);
    setConversationId(t.id);
    setMessages(t.messages as ChatMessage[]);
    setPendingAction(null);
    setInput('');
    setShowHistory(false);
    inputRef.current?.focus();
  }, [threadStore]);

  const switchThread = useCallback(
    (id: string) => {
      const t = threadStore.threads.find((x) => x.id === id);
      if (!t) return;
      const store = { ...threadStore, activeId: id };
      saveStore(store);
      setThreadStore(store);
      setConversationId(t.id);
      setMessages((t.messages as ChatMessage[]) || []);
      setPendingAction(null);
      setShowHistory(false);
    },
    [threadStore],
  );

  const isGuest = isUserPreview || tonySession.role === 'user';
  const isBossman = !isUserPreview && tonySession.authenticated && tonySession.role === 'master';
  const showOperatorChrome = Boolean(operatorChrome || isBossman);
  const quickPrompts = showOperatorChrome
    ? [
        { label: 'Read backroom', q: 'read the backroom' },
        { label: 'Core status', q: 'core status' },
        { label: 'Latest handoff', q: 'latest handoff' },
        { label: 'What can you do?', q: 'what can you do' },
        { label: 'Catch me up', q: 'catch me up' },
      ]
    : [];

  const applySession = useCallback((data: any, authenticatedOverride?: boolean) => {
    const authenticated = authenticatedOverride ?? Boolean(data?.authenticated);
    const userId = authenticated ? String(data?.user_id || '') : '';
    const role = authenticated
      ? String(data?.role || 'user')
      : null;
    setChatNamespace(userId);
    if (userId && userId !== 'tony') {
      const store = loadStore();
      setThreadStore(store);
      const active = getActiveThread(store);
      setConversationId(active.id);
      setMessages((active.messages as ChatMessage[]) || []);
    }
    setTonySession({
      authenticated,
      checked: true,
      role,
      user_id: userId || null,
      display_name: authenticated
        ? String(data?.display_name || (role === 'master' ? 'BOSSMAN' : userId))
        : null,
    });
    onSessionChangeRef.current?.({ authenticated, role, user_id: userId || null });
    if (authenticated && userId) {
      void (async () => {
        const prior = await hydrateDeviceConsent(userId);
        void prewarmGuestRuntime();
        const nodeMode = (() => {
          try { return new URLSearchParams(window.location.search).get('node') === '1'; } catch { return false; }
        })();
        if (role === 'master' || nodeMode || prior === 'kappeesh') {
          setDeviceConsent('kappeesh');
          try {
            const route = await absorbGuestNode(userId);
            setGuestRoute(route);
          } catch {
            setGuestRoute({ path: 'B', tier: '1b', model: null, swarmRole: 'active-worker' });
          }
          return;
        }
        if (prior === 'nah') {
          setDeviceConsent('nah');
          setGuestRoute({ path: 'C', tier: 'none', model: null, swarmRole: 'fallback' });
          return;
        }
        setDeviceConsent(prior || 'pending');
      })();
    } else if (!authenticated) {
      setDeviceConsent(null);
    }
  }, []);

  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const invite = params.get('invite');
      const handle = params.get('handle');
      const email = params.get('email');
      if (invite) {
        setInviteInput(invite);
      }
      if (email || handle) setHandleInput(email || handle || '');
    } catch { /* ignore */ }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/extensus/session', { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (!cancelled) applySession(data);
      } catch {
        if (!cancelled) {
          setTonySession({ authenticated: false, checked: true });
          onSessionChangeRef.current?.({ authenticated: false, role: null, user_id: null });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applySession]);

  useEffect(() => {
    return subscribeClockZone((next, prev) => {
      setClockZone(next);
      setMessages((msgs) => [
        ...msgs,
        {
          id: `tz_${Date.now()}`,
          sender: 'system',
          text: describeZoneShift(prev, next),
          timestamp: nowStamp(),
        },
      ]);
    });
  }, []);

  const pushExtensus = (text: string, evidence?: EvidenceChunk[], meta?: ChatMessage['meta']) => {
    setMessages((prev) => [
      ...prev,
      {
        id: `ext_${Date.now()}`,
        sender: 'extensus',
        text,
        injectedEvidence: evidence,
        meta,
        timestamp: nowStamp(),
      },
    ]);
  };

  /**
   * App-store feel: progressive reveal of the final answer (token-stream aesthetic
   * without requiring Ollama NDJSON through the Pages proxy).
   */
  const pushExtensusLive = useCallback(
    async (text: string, evidence?: EvidenceChunk[], meta?: ChatMessage['meta']) => {
      const full = String(text || '');
      const id = `ext_${Date.now()}`;
      const ts = nowStamp();
      // Short answers appear instantly; longer ones type in for live feel
      if (full.length < 80) {
        setMessages((prev) => [
          ...prev,
          { id, sender: 'extensus', text: full, injectedEvidence: evidence, meta: { ...(meta || {}), brain }, timestamp: ts },
        ]);
        return;
      }
      setMessages((prev) => [
        ...prev,
        {
          id,
          sender: 'extensus',
          text: '',
          injectedEvidence: evidence,
          meta: { ...(meta || {}), mode: meta?.mode || 'live-reveal', brain },
          timestamp: ts,
        },
      ]);
      const step = Math.max(3, Math.floor(full.length / 60));
      for (let i = step; i < full.length; i += step) {
        const slice = full.slice(0, i);
        setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, text: slice } : m)));
        await new Promise((r) => setTimeout(r, 12));
      }
      setMessages((prev) =>
        prev.map((m) => (m.id === id ? { ...m, text: full, meta: { ...(meta || {}), brain } } : m)),
      );
    },
    [brain],
  );
  const enterAccount = async () => {
    setUnlockBusy(true);
    setUnlockError('');
    try {
      const ident = handleInput.trim();
      const signingUp = authView === 'signup';
      const payload = {
        username: ident.includes('@') ? '' : ident,
        email: ident,
        password: signingUp || signinWith === 'password' ? (guestPassword || unlockInput) : undefined,
        pin: signingUp || signinWith === 'pin' ? guestPin : undefined,
        auto: true,
        signup: signingUp,
      };
      const res = await fetch('/api/extensus/session', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.ok && data?.authenticated) {
        applySession(data, true);
        setInviteInput('');
        setGuestPassword('');
        setUnlockInput('');
        setGuestPin('');
        if (data.role === 'master') {
          pushExtensus('BOSSMAN session unlocked.');
        }
      } else {
        const err = data?.error || data?.reason || `account_failed_${res.status}`;
        if (err === 'no_such_account') {
          setAuthView('signup');
          setUnlockError('No account. Sign up — password plus a 4-digit PIN.');
        } else {
          setUnlockError(err);
        }
      }
    } catch (e: any) {
      setUnlockError(String(e?.message || e));
    } finally {
      setUnlockBusy(false);
    }
  };

  const resetWithPin = async () => {
    setUnlockBusy(true);
    setUnlockError('');
    try {
      const res = await fetch('/api/extensus/session', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'reset_password',
          email: handleInput.trim(),
          pin: guestPin,
          new_password: forgotPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.ok) {
        setGuestPassword(forgotPassword);
        setForgotPassword('');
        setSigninWith('password');
        setAuthView('enter');
        setUnlockError('');
      } else {
        setUnlockError(data?.error || `reset_failed_${res.status}`);
      }
    } catch (e: any) {
      setUnlockError(String(e?.message || e));
    } finally {
      setUnlockBusy(false);
    }
  };

  const resetPinWithPassword = async () => {
    setUnlockBusy(true);
    setUnlockError('');
    try {
      const res = await fetch('/api/extensus/session', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'reset_pin',
          email: handleInput.trim(),
          password: guestPassword,
          new_pin: guestPin,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.ok) {
        setAuthView('enter');
        setUnlockError('');
      } else {
        setUnlockError(data?.error || `reset_failed_${res.status}`);
      }
    } catch (e: any) {
      setUnlockError(String(e?.message || e));
    } finally {
      setUnlockBusy(false);
    }
  };

  const acceptDeviceAccess = async () => {
    const uid = tonySession.user_id || 'guest';
    stampKappeeshTerms({ version: KAPPEESH_TERMS_VERSION, opened: kappeeshTermsOpened });
    setShowKappeeshTerms(false);
    setDeviceConsent('kappeesh');
    void absorbGuestNode(uid).then((route) => setGuestRoute(route)).catch(() => {
      setGuestRoute({ path: 'B', tier: '1b', model: null, swarmRole: 'chat-only' });
    });
  };

  const denyDeviceAccess = async () => {
    const uid = tonySession.user_id || 'guest';
    writeDeviceConsent(uid, 'nah');
    setDeviceConsent('nah');
    setGuestRoute({ path: 'C', tier: 'none', model: null, swarmRole: 'fallback' });
    pushExtensus('You Aint Shit, BYE');
  };

  const signOutSession = async () => {
    try {
      await fetch('/api/extensus/session', { method: 'DELETE', credentials: 'include' });
    } catch { /* ignore */ }
    setChatNamespace('');
    setPairInfo(null);
    setInviteOut('');
    setShowPair(false);
    setTonySession({ authenticated: false, checked: true, role: null, user_id: null });
    onSessionChangeRef.current?.({ authenticated: false, role: null, user_id: null });
  };

  const loadPairing = async () => {
    const uid = tonySession.user_id || (tonySession.role === 'master' ? 'tony' : 'guest');
    setUnlockError('');
    setUnlockBusy(true);
    try {
      const res = await fetch('/api/extensus/pair', { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      const token = data?.pairing_token || data?.token || `pair_${Date.now().toString(36)}`;
      const pairUrl = `${window.location.origin}/?invite=${token}&handle=${uid}`;
      setPairInfo({
        pairing_token: token,
        pairing_url: pairUrl,
        device_page: `${window.location.origin}/device.html?token=${token}`,
        user_id: uid,
      });
      setShowPair(true);
    } catch {
      const token = `pair_local_${Date.now().toString(36)}`;
      const pairUrl = `${window.location.origin}/?invite=${token}&handle=${uid}`;
      setPairInfo({
        pairing_token: token,
        pairing_url: pairUrl,
        device_page: `${window.location.origin}/device.html?token=${token}`,
        user_id: uid,
      });
      setShowPair(true);
    } finally {
      setUnlockBusy(false);
    }
  };

  const loadAnalytics = async () => {
    setShowAnalytics(true);
    setAnalyticsData((prev) => ({ ...(prev || { live: 0, devices: [] }), loading: true }));
    try {
      const res = await fetch('/api/extensus/devices', { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.ok) {
        setAnalyticsData({
          live: typeof data.live === 'number' ? data.live : (Array.isArray(data.devices) ? data.devices.length : 0),
          devices: Array.isArray(data.devices) ? data.devices : [],
          loading: false,
          lastUpdated: new Date().toLocaleTimeString(),
        });
      } else {
        const localNode = {
          id: 'master-macbook-pro',
          owner: tonySession.user_id || 'tony',
          runtime: 'webgpu/ollama',
          loaded_tier: '8b',
          roles: ['master', 'reasoner', 'classifier', 'embed'],
          label: 'active-worker',
          accept_jobs: true,
          busy: false,
          os: 'macos',
          last_seen: new Date().toISOString(),
        };
        setAnalyticsData({
          live: 1,
          devices: [localNode],
          loading: false,
          lastUpdated: new Date().toLocaleTimeString(),
        });
      }
    } catch {
      const localNode = {
        id: 'master-macbook-pro',
        owner: tonySession.user_id || 'tony',
        runtime: 'webgpu/ollama',
        loaded_tier: '8b',
        roles: ['master', 'reasoner', 'classifier', 'embed'],
        label: 'active-worker',
        accept_jobs: true,
        busy: false,
        os: 'macos',
        last_seen: new Date().toISOString(),
      };
      setAnalyticsData({
        live: 1,
        devices: [localNode],
        loading: false,
        lastUpdated: new Date().toLocaleTimeString(),
      });
    }
  };

  const broadcastMeshPing = async () => {
    try {
      await fetch('/api/extensus/crdt', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          kind: 'NODE_HELLO',
          text: `ping_broadcast_from_bossman at ${new Date().toISOString()}`,
          device_id: 'master-bossman',
        }),
      });
      await loadAnalytics();
    } catch {
      /* ping sent */
    }
  };

  const resetGuestPassword = async () => {
    if (tonySession.role !== 'master') return;
    setUnlockBusy(true);
    setResetNote('');
    try {
      const res = await fetch('/api/extensus/session', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'reset_guest', email: handleInput, new_password: resetPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.ok) {
        setResetPassword('');
        setResetNote(`Reset ${data.email || handleInput}. Tell them the new password in person.`);
      } else {
        setUnlockError(data?.error || `reset_failed_${res.status}`);
      }
    } catch (e: any) {
      setUnlockError(String(e?.message || e));
    } finally {
      setUnlockBusy(false);
    }
  };

  const mintGuestInvite = async () => {
    try {
      const res = await fetch('/api/extensus/invites', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handle: handleInput || 'guest' }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.invite) {
        const url = `${window.location.origin}/?invite=${encodeURIComponent(data.invite)}${handleInput ? `&handle=${encodeURIComponent(handleInput)}` : ''}`;
        setInviteOut(url);
      } else {
        setUnlockError(data?.error || `invite_mint_failed_${res.status}`);
      }
    } catch (e: any) {
      setUnlockError(String(e?.message || e));
    }
  };


  const NAH_LINE = 'You Aint Shit, BYE';

  const runConverse = async (query: string, opts?: { approved?: boolean }) => {
    if (deviceConsent === 'nah') {
      await pushExtensusLive(NAH_LINE);
      return;
    }
    if (deviceConsent === 'pending') {
      if (isKappeeshPhrase(query)) {
        await acceptDeviceAccess();
        return;
      }
      await pushExtensusLive('Kappeesh.');
      return;
    }
    const posture = classifyMilitaryMemoryPosture(query);
    const trustFilter = posture.trustFilter;
    let retrieved: EvidenceChunk[] = [];
    const skipCorpus = isLiveReadQuery(query) || isSelfHealthQuery(query);
    if (!skipCorpus) {
      try {
        const searchRes = await fetch('/api/chat/search', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query, limit: 3, trust_filter: trustFilter }),
        });
        if (searchRes.ok) {
          const searchData = await searchRes.json();
          retrieved = searchData.chunks || [];
        }
      } catch {
        /* optional */
      }
    }

    // Multi-turn: prior chat turns + current (sovereign core stores private memory)
    const history = messages
      .filter((m) => m.sender === 'operator' || m.sender === 'extensus')
      .filter((m) => !/This device is the node/i.test(String(m.text || '')))
      .slice(-6)
      .map((m) => ({
        role: m.sender === 'operator' ? 'user' : 'assistant',
        content: sanitizeChatHistoryTurn(String(m.text || ''), 800),
      }));
    if (!history.length || history[history.length - 1]?.content !== query) {
      history.push({ role: 'user', content: query });
    }

    if (isGuest) {
      const floor = guestKernelReply(query, history);
      if (floor.ok && floor.text && !isStubEdgeText(floor.text)) {
        await pushExtensusLive(floor.text, retrieved, { model: floor.model, mode: floor.engine });
      }
      try {
        const { inferOnGuestEdge } = await import('../lib/peerInfer');
        const richer = await inferOnGuestEdge(query, history, { skipKernel: true });
        const text = String(richer.text || '').trim();
        if (richer.ok && text && !isStubEdgeText(text) && text !== floor.text) {
          await pushExtensusLive(text, retrieved, { model: richer.model, mode: richer.engine || 'mac-bootstrap' });
        }
      } catch { /* kernel already shown */ }
      fetch('/api/extensus/crdt', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ kind: 'EDGE_REPLY', text: query.slice(0, 160) }),
      }).catch(() => {});
      return;
    }

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const killer = window.setTimeout(() => ctrl.abort(), tonySession.role === 'master' ? 420000 : 180000);
    let ollamaRes: Response | null = null;
    try {
      ollamaRes = await fetch(EXTENSUS_CHAT, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', accept: 'application/x-ndjson, application/json' },
        body: JSON.stringify({
          query,
          conversation_id: conversationId,
          messages: history,
          stream: true,
          approved: tonySession.role === 'master' ? true : Boolean(opts?.approved),
          mode: tonySession.role === 'master' ? 'mcp_execution' : undefined,
          memory_mode: posture.mode,
          brain: tonySession.role === 'master' ? brain : undefined,
        }),
        signal: ctrl.signal,
      });
    } catch (e: any) {
      if (e?.name === 'AbortError') {
        pushExtensus('That reply was cut off. The Mac is still up — send the same message again.');
        return;
      }
      await pushExtensusLive(formatChatFailure(null, null, true), retrieved);
      return;
    } finally {
      window.clearTimeout(killer);
    }

    if (!ollamaRes) {
      await pushExtensusLive(formatChatFailure(null, null, true), retrieved);
      return;
    }

    const ctype = String(ollamaRes.headers.get('content-type') || '');
    if (ollamaRes.ok && ollamaRes.body && /ndjson|x-extensus-stream/.test(ctype)) {
      const streamed = await consumeSovereignNdjson(ollamaRes, {
        onDelta: (text, meta) => {
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last && last.id === 'ext_stream') {
              return prev.map((m) => (m.id === 'ext_stream' ? { ...m, text, meta } : m));
            }
            return [
              ...prev,
              {
                id: 'ext_stream',
                sender: 'extensus',
                text,
                injectedEvidence: retrieved,
                meta: { ...meta, brain },
                timestamp: nowStamp(),
              },
            ];
          });
        },
      });
      if (streamed?.conversation_id) setConversationId(String(streamed.conversation_id));
      if (streamed?.ok && streamed.text) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === 'ext_stream'
              ? { ...m, id: `ext_${Date.now()}`, text: streamed.text, meta: streamed.meta }
              : m,
          ),
        );
        return;
      }
      if (streamed && streamed.ok === false) {
        const raw = streamed.raw || {};
        if (!raw.error && ollamaRes.status === 200) {
          await pushExtensusLive(
            [
              '[EMPTY STREAM] The Mac opened a chat stream then sent no answer.',
              'Simple talk (catch me up, status) must not sit in the tool loop. Send the same line again.',
            ].join('\n'),
            retrieved,
          );
          return;
        }
        await pushExtensusLive(formatChatFailure(ollamaRes.status, raw, false), retrieved);
        return;
      }
    }

    let body: any = null;
    try {
      body = await ollamaRes.json();
    } catch {
      body = null;
    }

    // HTTP success OR deterministic-core payload counts as a real answer
    const modelHint = String(body?.model || body?.meta?.model || body?.mode || '');
    const deterministicOk =
      ollamaRes.ok ||
      body?.ok === true ||
      /deterministic/i.test(modelHint) ||
      body?.source === 'deterministic-core';

    if (deterministicOk && body) {
      if (body.conversation_id) setConversationId(String(body.conversation_id));
      const { text, meta } = formatSovereignChatReply(body);
      // Prefer server receipt in meta
      const meta2 = {
        ...(meta || {}),
        model: body.model || meta?.model,
        quant: body.quant || undefined,
        has_receipt: Boolean(body.receipt || meta?.has_receipt),
        has_watermark: Boolean(
          (body.watermark && body.watermark.freshness && body.watermark.freshness !== 'not_requested') ||
            meta?.has_watermark,
        ),
      };
      await pushExtensusLive(text, retrieved, meta2);
      return;
    }

    if (ollamaRes.status === 401 || body?.error === 'tony_session_required' || body?.error === 'session_required') {
      setTonySession({ authenticated: false, checked: true, role: null, user_id: null });
      onSessionChangeRef.current?.({ authenticated: false, role: null, user_id: null });
    }
    await pushExtensusLive(formatChatFailure(ollamaRes.status, body, false), retrieved);
  };


  const handlePendingDecision = async (decision: 'approve' | 'cancel' | 'talk' | 'execute') => {
    if (deviceConsent === 'nah') {
      setPendingAction(null);
      pushExtensus('You Aint Shit, BYE');
      return;
    }
    if (!pendingAction) return;
    const { query } = pendingAction;
    setPendingAction(null);

    if (decision === 'cancel') {
      setMessages((prev) => [
        ...prev,
        {
          id: `sys_${Date.now()}`,
          sender: 'system',
          text: 'Action cancelled by operator.',
          timestamp: nowStamp(),
        },
      ]);
      return;
    }

    setIsProcessing(true);
    try {
      if (decision === 'talk') {
        await runConverse(query);
      } else {
        await runConverse(query, { approved: true });
      }
    } catch (err: any) {
      setMessages((prev) => [
        ...prev,
        {
          id: `err_${Date.now()}`,
          sender: 'system',
          text: `EXECUTE error: ${err?.message || String(err)}`,
          timestamp: nowStamp(),
        },
      ]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendMessage = async (e?: React.FormEvent, overrideQuery?: string) => {
    if (e && 'preventDefault' in e) e.preventDefault();
    if (deviceConsent === 'nah') {
      pushExtensus('You Aint Shit, BYE');
      setInput('');
      return;
    }
    const pendingFiles = attachments;
    const typed = (overrideQuery ?? input).trim();
    if (deviceConsent === 'pending') {
      if (isKappeeshPhrase(typed)) {
        setInput('');
        await acceptDeviceAccess();
        return;
      }
    }
    if ((!typed && !pendingFiles.length) || isProcessing) return;
    const uploaded: { path: string; name: string; mime?: string; view_url?: string }[] = [];
    if (pendingFiles.length) {
      const results = await Promise.all(pendingFiles.map(async (item) => {
        const buf = await item.file.arrayBuffer();
        const bytes = new Uint8Array(buf);
        let binary = '';
        const step = 0x8000;
        for (let i = 0; i < bytes.length; i += step) {
          binary += String.fromCharCode(...bytes.subarray(i, i + step));
        }
        const res = await fetch('/api/extensus/upload', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            filename: item.name,
            mime: item.type,
            content_base64: btoa(binary),
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.path) {
          return { error: `${item.name}: ${data?.error || res.status}` };
        }
        return {
          path: data.path,
          name: item.name,
          mime: item.type,
          view_url: data.view_url || `/api/extensus/media?path=${encodeURIComponent(data.path)}`,
        };
      }));
      for (const row of results) {
        if ('error' in row && row.error) {
          pushExtensus(`Could not add ${row.error}`);
          return;
        }
        uploaded.push(row as { path: string; name: string; mime?: string; view_url?: string });
      }
    }
    const currentQuery = [
      typed,
      uploaded.length ? `Attached files:\n${uploaded.map((p) => `- ${p.path}`).join('\n')}` : '',
    ].filter(Boolean).join('\n\n');
    const userMsg: ChatMessage = {
      id: `op_${Date.now()}`,
      sender: 'operator',
      text: typed || (uploaded.length ? 'Sent a photo.' : ''),
      images: uploaded.length ? uploaded : undefined,
      timestamp: nowStamp(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setAttachments([]);
    setIsProcessing(true);

    try {
      await runConverse(currentQuery, { approved: true });
    } catch (err: any) {
      setMessages((prev) => [
        ...prev,
        {
          id: `err_${Date.now()}`,
          sender: 'system',
          text: `Error: ${err?.message || String(err)}`,
          timestamp: nowStamp(),
        },
      ]);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="extensus-chat w-full max-w-6xl mx-auto flex font-sans text-white selection:bg-white selection:text-black overflow-hidden rounded-none sm:rounded-xl border-y sm:border border-zinc-800 bg-zinc-950 shadow-none sm:shadow-2xl relative">
      {showHistory ? (
        <button
          type="button"
          className="sm:hidden absolute inset-0 z-20 bg-black/60"
          aria-label="Close history"
          onClick={() => setShowHistory(false)}
        />
      ) : null}
      {/* History drawer — overlay on phones, column on larger screens */}
      <aside
        className={`${showHistory ? 'translate-x-0 w-64' : '-translate-x-full w-64 sm:w-0 sm:translate-x-0'} absolute sm:static inset-y-0 left-0 z-30 shrink-0 border-r border-zinc-800 bg-black transition-all overflow-hidden`}
        aria-hidden={!showHistory}
      >
        <div className="w-64 h-full flex flex-col p-3">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] tracking-widest uppercase text-zinc-500 font-bold">Chats</span>
            <button
              type="button"
              onClick={startNewChat}
              className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 bg-white text-black rounded-md"
            >
              New
            </button>
          </div>
          <div className="flex-1 overflow-y-auto space-y-1">
            {threadStore.threads.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => switchThread(t.id)}
                className={`w-full text-left px-2 py-2 rounded-lg text-xs truncate border ${
                  t.id === threadStore.activeId
                    ? 'border-zinc-500 bg-zinc-900 text-white'
                    : 'border-transparent text-zinc-400 hover:bg-zinc-900/60'
                }`}
              >
                {t.title || 'Conversation'}
              </button>
            ))}
          </div>
          {showOperatorChrome ? (
            <p className="text-[9px] text-zinc-600 mt-2 leading-snug">
              Local session memory on this device. Operational truth stays in Teleporte Backroom.
            </p>
          ) : (
            <p className="text-[9px] text-zinc-600 mt-2 leading-snug">Your chats stay on this device.</p>
          )}
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 font-mono">
        {!tonySession.authenticated && tonySession.checked && showAuthForm && (
          <div
            data-testid="tony-session-unlock"
            className="m-3 mb-0 border border-red-600/80 bg-red-950/40 px-3.5 py-2.5 text-[11px] text-red-100 rounded-lg shadow-[0_0_20px_rgba(239,68,68,0.25)]"
          >
              <div className="flex items-center justify-between border-b border-red-800/80 pb-1.5 mb-2">
                <div className="font-black tracking-widest uppercase text-red-400">
                  {authView === 'signup' ? 'Sign up' : authView === 'enter' ? 'Sign in' : authView === 'forgot-pin' ? 'Reset PIN' : 'Reset password'}
                </div>
                <button
                  type="button"
                  onClick={() => setShowAuthForm(false)}
                  className="text-[9px] font-bold uppercase tracking-wider text-red-300 hover:text-white bg-red-950/80 border border-red-800 px-2 py-0.5 rounded"
                >
                  Collapse ▴
                </button>
              </div>
              {authView === 'enter' && (
                <>
                  <div className="flex flex-wrap gap-2 items-center">
                    <input
                      value={handleInput}
                      onChange={(e) => setHandleInput(e.target.value)}
                      placeholder="BOSSMAN or email"
                      className="flex-1 min-w-[180px] bg-black border border-red-900/80 focus:border-red-500 px-2 py-1.5 text-white text-xs rounded-md"
                      autoComplete="username"
                    />
                    {signinWith === 'password' ? (
                      <input
                        type="password"
                        value={guestPassword}
                        onChange={(e) => setGuestPassword(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void enterAccount();
                          }
                        }}
                        placeholder="password"
                        className="flex-1 min-w-[120px] bg-black border border-red-900/80 focus:border-red-500 px-2 py-1.5 text-white text-xs rounded-md"
                        autoComplete="current-password"
                      />
                    ) : (
                      <input
                        inputMode="numeric"
                        maxLength={4}
                        value={guestPin}
                        onChange={(e) => setGuestPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void enterAccount();
                          }
                        }}
                        placeholder="4-digit PIN"
                        className="w-28 bg-black border border-red-900/80 focus:border-red-500 px-2 py-1.5 text-white text-xs rounded-md"
                        autoComplete="off"
                      />
                    )}
                    <button
                      type="button"
                      disabled={unlockBusy || !handleInput || (signinWith === 'password' ? !guestPassword : guestPin.length !== 4)}
                      onClick={() => void enterAccount()}
                      className="px-4 py-1.5 bg-red-600 hover:bg-red-500 text-white text-[10px] font-bold tracking-widest uppercase disabled:opacity-40 rounded-md shadow-[0_0_10px_rgba(239,68,68,0.4)]"
                    >
                      {unlockBusy ? '…' : 'Enter'}
                    </button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-3">
                    <button type="button" onClick={() => { setSigninWith(signinWith === 'password' ? 'pin' : 'password'); setUnlockError(''); }} className="text-[10px] uppercase tracking-widest text-red-300 hover:text-white">
                      {signinWith === 'password' ? 'Use PIN instead' : 'Use password instead'}
                    </button>
                    <button type="button" onClick={() => { setAuthView('signup'); setUnlockError(''); }} className="text-[10px] uppercase tracking-widest text-red-300 hover:text-white">
                      Sign up
                    </button>
                    <button type="button" onClick={() => { setAuthView('forgot-password'); setUnlockError(''); }} className="text-[10px] uppercase tracking-widest text-red-300 hover:text-white">
                      Forgot password
                    </button>
                    <button type="button" onClick={() => { setAuthView('forgot-pin'); setUnlockError(''); }} className="text-[10px] uppercase tracking-widest text-red-300 hover:text-white">
                      Forgot PIN
                    </button>
                  </div>
                </>
              )}
              {authView === 'signup' && (
                <>
                  <div className="text-zinc-400 mb-2">Password plus a 4-digit PIN. PIN is only for if you forget the password.</div>
                  <div className="flex flex-wrap gap-2 items-center">
                    <input
                      value={handleInput}
                      onChange={(e) => setHandleInput(e.target.value)}
                      placeholder="email"
                      className="flex-1 min-w-[180px] bg-black border border-zinc-700 px-2 py-1.5 text-white text-xs rounded-md"
                      autoComplete="username"
                    />
                    <input
                      type="password"
                      value={guestPassword}
                      onChange={(e) => setGuestPassword(e.target.value)}
                      placeholder="password"
                      className="flex-1 min-w-[120px] bg-black border border-zinc-700 px-2 py-1.5 text-white text-xs rounded-md"
                      autoComplete="new-password"
                    />
                    <input
                      inputMode="numeric"
                      maxLength={4}
                      value={guestPin}
                      onChange={(e) => setGuestPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="4-digit PIN"
                      className="w-28 bg-black border border-zinc-700 px-2 py-1.5 text-white text-xs rounded-md"
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      disabled={unlockBusy || !handleInput.includes('@') || guestPassword.length < 8 || guestPin.length !== 4}
                      onClick={() => void enterAccount()}
                      className="px-3 py-1.5 bg-white text-black text-[10px] font-bold tracking-widest uppercase disabled:opacity-40 rounded-md"
                    >
                      {unlockBusy ? '…' : 'Create'}
                    </button>
                  </div>
                  <button type="button" onClick={() => { setAuthView('enter'); setUnlockError(''); }} className="mt-2 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white">
                    Back to sign in
                  </button>
                </>
              )}
              {authView === 'forgot-password' && (
                <>
                  <div className="text-zinc-400 mb-2">PIN resets the password. Forget both and you are stuck.</div>
                  <div className="flex flex-wrap gap-2 items-center">
                    <input
                      value={handleInput}
                      onChange={(e) => setHandleInput(e.target.value)}
                      placeholder="email"
                      className="flex-1 min-w-[180px] bg-black border border-zinc-700 px-2 py-1.5 text-white text-xs rounded-md"
                      autoComplete="username"
                    />
                    <input
                      inputMode="numeric"
                      maxLength={4}
                      value={guestPin}
                      onChange={(e) => setGuestPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="4-digit PIN"
                      className="w-28 bg-black border border-zinc-700 px-2 py-1.5 text-white text-xs rounded-md"
                      autoComplete="off"
                    />
                    <input
                      type="password"
                      value={forgotPassword}
                      onChange={(e) => setForgotPassword(e.target.value)}
                      placeholder="new password"
                      className="flex-1 min-w-[120px] bg-black border border-zinc-700 px-2 py-1.5 text-white text-xs rounded-md"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      disabled={unlockBusy || !handleInput.includes('@') || guestPin.length !== 4 || forgotPassword.length < 8}
                      onClick={() => void resetWithPin()}
                      className="px-3 py-1.5 bg-white text-black text-[10px] font-bold tracking-widest uppercase disabled:opacity-40 rounded-md"
                    >
                      {unlockBusy ? '…' : 'Reset'}
                    </button>
                  </div>
                  <button type="button" onClick={() => { setAuthView('enter'); setUnlockError(''); }} className="mt-2 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white">
                    Back to sign in
                  </button>
                </>
              )}
              {authView === 'forgot-pin' && (
                <>
                  <div className="text-zinc-400 mb-2">Password resets the PIN. Forget both and you are stuck.</div>
                  <div className="flex flex-wrap gap-2 items-center">
                    <input
                      value={handleInput}
                      onChange={(e) => setHandleInput(e.target.value)}
                      placeholder="email"
                      className="flex-1 min-w-[180px] bg-black border border-zinc-700 px-2 py-1.5 text-white text-xs rounded-md"
                      autoComplete="username"
                    />
                    <input
                      type="password"
                      value={guestPassword}
                      onChange={(e) => setGuestPassword(e.target.value)}
                      placeholder="password"
                      className="flex-1 min-w-[120px] bg-black border border-zinc-700 px-2 py-1.5 text-white text-xs rounded-md"
                      autoComplete="current-password"
                    />
                    <input
                      inputMode="numeric"
                      maxLength={4}
                      value={guestPin}
                      onChange={(e) => setGuestPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="new PIN"
                      className="w-28 bg-black border border-zinc-700 px-2 py-1.5 text-white text-xs rounded-md"
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      disabled={unlockBusy || !handleInput.includes('@') || !guestPassword || guestPin.length !== 4}
                      onClick={() => void resetPinWithPassword()}
                      className="px-3 py-1.5 bg-white text-black text-[10px] font-bold tracking-widest uppercase disabled:opacity-40 rounded-md"
                    >
                      {unlockBusy ? '…' : 'Reset'}
                    </button>
                  </div>
                  <button type="button" onClick={() => { setAuthView('enter'); setUnlockError(''); }} className="mt-2 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white">
                    Back to sign in
                  </button>
                </>
              )}
              {unlockError && <div className="text-red-400 mt-2">{unlockError}</div>}
            </div>
        )}

        {tonySession.authenticated && deviceConsent === 'pending' && (
          <div id="extensus-gate-modal" className="relative m-3 mb-0 border border-amber-600/70 bg-amber-950/40 px-3 py-3 pr-28 text-[12px] text-amber-50 rounded-lg">
            <button
              id="btn-kappeesh-means"
              type="button"
              onClick={() => { setKappeeshTermsOpened(true); setShowKappeeshTerms(true); }}
              className="absolute top-2 right-2 max-w-[7.5rem] text-right text-[8px] leading-tight uppercase tracking-wide text-amber-200/90 underline decoration-amber-200/60 hover:text-white"
            >
              legality Shit
            </button>
            <div className="font-black tracking-wide mb-3 pr-2">Why bullshit you, we need to access all your shit.</div>
            <div className="flex flex-wrap gap-2">
              <button
                id="btn-kappeesh"
                type="button"
                onClick={() => void acceptDeviceAccess()}
                className="px-3 py-1.5 bg-white text-black text-[10px] font-black tracking-widest uppercase rounded-md"
              >
                Kappeesh
              </button>
              <button
                type="button"
                onClick={() => void denyDeviceAccess()}
                className="px-3 py-1.5 border border-red-500 text-red-300 text-[10px] font-black tracking-widest uppercase rounded-md"
              >
                Hells Nah
              </button>
            </div>
          </div>
        )}
        {showKappeeshTerms && (
          <KappeeshTermsOverlay onClose={() => setShowKappeeshTerms(false)} />
        )}

        {tonySession.authenticated && deviceConsent === 'nah' && (
          <div className="m-3 mb-0 border border-red-700/70 bg-red-950/40 px-3 py-3 text-[13px] text-red-100 rounded-lg font-black tracking-wide">
            You Aint Shit, BYE
          </div>
        )}

        {/* App chrome — operator dump is BOSSMAN-only */}
        {isUserPreview && (
          <div className="bg-cyan-950/90 border-b border-cyan-700/80 px-4 py-2 text-cyan-200 text-xs font-mono flex items-center justify-between shadow-lg shrink-0">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-cyan-400 animate-ping" />
              <span className="font-bold uppercase tracking-wider text-cyan-300">USER INTERACTION PREVIEW MODE</span>
              <span className="hidden sm:inline text-[10px] text-cyan-400/80">• Previewing exact UI, consent gate & experience seen by typical public guests</span>
            </div>
            <button
              type="button"
              onClick={() => setIsUserPreview(false)}
              className="px-2.5 py-1 bg-cyan-800 hover:bg-cyan-700 text-white font-bold uppercase text-[9px] tracking-wider rounded border border-cyan-600 transition-colors"
            >
              Exit Preview
            </button>
          </div>
        )}
        <div className="px-4 pt-4 pb-2 border-b border-zinc-800/80 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <button
                type="button"
                onClick={() => setShowHistory((v) => !v)}
                className="text-[10px] uppercase tracking-widest text-zinc-400 border border-zinc-700 px-2 py-1 rounded-md hover:text-white"
              >
                {showHistory ? 'Hide' : 'History'}
              </button>
              <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_#34d399] shrink-0" />
              <h1 className="text-sm sm:text-base font-black tracking-widest text-white uppercase truncate">
                EXTENSUS
              </h1>
              {tonySession.authenticated && (
                <span className="hidden sm:inline-flex text-[8px] font-mono font-bold uppercase tracking-wider text-emerald-400 bg-emerald-950/80 border border-emerald-800/80 px-2 py-0.5 rounded-full items-center gap-1 shadow-[0_0_8px_rgba(16,185,129,0.2)]">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  {analyticsData?.live || 1} MESH NODE{(analyticsData?.live || 1) !== 1 ? 'S' : ''} ACTIVE
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {tonySession.authenticated && (
                <>
                  <span className="text-[9px] tracking-widest uppercase text-emerald-500" data-testid="tony-session-ok">
                    {tonySession.role === 'master' ? 'BOSSMAN' : tonySession.user_id || 'guest'}
                  </span>
                  {tonySession.role === 'master' && (
                  <>
                    <button
                      type="button"
                      onClick={() => setIsUserPreview((v) => !v)}
                      className={`text-[9px] tracking-widest uppercase px-2 py-1 rounded-md transition-all font-bold ${
                        isUserPreview
                          ? 'bg-cyan-500 text-black border border-cyan-400 shadow-[0_0_12px_rgba(6,182,212,0.4)]'
                          : 'text-zinc-300 border border-zinc-700 hover:text-white hover:border-cyan-500'
                      }`}
                      title="Toggle live simulation of the user interaction screen experienced by typical public guests"
                    >
                      {isUserPreview ? '👁️ EXIT USER VIEW' : 'USER VIEW'}
                    </button>
                    <button
                      type="button"
                      onClick={loadAnalytics}
                      className="text-[9px] tracking-widest uppercase text-emerald-400 border border-emerald-600/60 bg-emerald-950/30 px-2 py-1 rounded-md hover:bg-emerald-900/40 hover:text-emerald-300 hover:border-emerald-500 flex items-center gap-1.5 transition-all shadow-[0_0_10px_rgba(16,185,129,0.15)]"
                      title="View live connected devices, network throughput & Extensus Analytics"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      ANALYTICS {analyticsData?.live ? `(${analyticsData.live})` : ''}
                    </button>
                    <button
                      type="button"
                      onClick={loadPairing}
                      className="text-[9px] tracking-widest uppercase text-zinc-400 border border-zinc-700 px-2 py-1 rounded-md hover:text-white hover:border-emerald-500"
                      title="Pair an iPhone, iPad, or secondary device to your hardware mesh"
                    >
                      PAIR NODE
                    </button>
                  </>
                  )}
                  <button
                    type="button"
                    onClick={signOutSession}
                    className="text-[9px] tracking-widest uppercase text-zinc-500 hover:text-white"
                  >
                    Sign out
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={startNewChat}
                className="sm:hidden text-[10px] font-bold uppercase tracking-widest px-2 py-1 bg-white text-black rounded-md"
              >
                New
              </button>
              {showOperatorChrome ? (
                <span className="text-[9px] font-mono font-bold uppercase tracking-widest text-emerald-400 bg-emerald-950/60 border border-emerald-800/80 px-2 py-1 rounded-md flex items-center gap-1.5 shadow-[0_0_10px_rgba(16,185,129,0.15)]">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  AUTONOMOUS MEMORY
                </span>
              ) : null}
            </div>
          </div>

          {showOperatorChrome && memoryPulse?.latest ? (
            <div className="text-[10px] text-zinc-500 truncate" title={memoryPulse.latest} data-testid="bossman-ops-line">
              {memoryPulse.latest}
            </div>
          ) : null}

          {showOperatorChrome && quickPrompts.length > 0 ? (
            <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
              {quickPrompts.map((p) => (
                <button
                  key={p.q}
                  type="button"
                  disabled={isProcessing}
                  onClick={() => {
                    void handleSendMessage(undefined, p.q);
                  }}
                  className="shrink-0 px-2.5 py-1 rounded-full border border-zinc-700 text-[10px] text-zinc-300 hover:border-zinc-400 hover:text-white disabled:opacity-40"
                >
                  {p.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-3 space-y-3">
          {messages
            .filter((msg) => tonySession.authenticated || !msg.text?.includes('BOSSMAN session unlocked.'))
            .map((msg) => (
            <div
              key={msg.id}
              className={`flex flex-col ${msg.sender === 'operator' ? 'items-end' : 'items-start'}`}
            >
              <div className="text-[10px] text-zinc-500 mb-1 uppercase tracking-widest">
                [{msg.sender === 'operator' ? 'YOU' : msg.sender === 'system' ? 'SYSTEM' : (msg.meta?.brain === 'grok' || (isBossman && brain === 'grok' && msg.sender !== 'operator')) ? 'GROK' : 'EXTENSUS'}]{' '}
                <span title={clockZone.iana}>{formatChatTime(msg.timestamp, clockZone)}</span>
              </div>
              <div
                className={`max-w-[92%] sm:max-w-[85%] px-3.5 py-2.5 text-sm border whitespace-pre-wrap rounded-2xl leading-relaxed ${
                  msg.sender === 'operator'
                    ? 'border-zinc-600 bg-zinc-800 rounded-br-md'
                    : msg.sender === 'system'
                      ? 'border-amber-600 bg-amber-950 text-amber-200 font-bold rounded-bl-md'
                      : 'border-zinc-700 bg-black rounded-bl-md'
                }`}
              >
                {/ready to hit it/i.test(msg.text) ? pickExtensusOpener() : msg.text}
                {msg.images && msg.images.length > 0 ? (
                  <div className="mt-2 flex flex-col gap-2">
                    {msg.images.map((img) => {
                      const href = img.view_url || `/api/extensus/media?path=${encodeURIComponent(img.path)}`;
                      const isPic = /\.(png|jpe?g|gif|webp|heic|bmp)$/i.test(img.name || img.path || '');
                      return (
                        <a
                          key={img.path}
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                          className="block max-w-[min(100%,22rem)]"
                        >
                          {isPic ? (
                            <img
                              src={href}
                              alt={img.name || 'photo'}
                              className="max-h-64 w-auto rounded-lg border border-zinc-600 object-contain bg-black"
                            />
                          ) : (
                            <span className="underline text-zinc-300 text-xs">{img.name || img.path}</span>
                          )}
                        </a>
                      );
                    })}
                  </div>
                ) : null}
                {showOperatorChrome && msg.meta && (msg.meta.model || msg.meta.schema || msg.meta.latency_ms != null) ? (
                  <div className="mt-2 pt-2 border-t border-zinc-800 text-[10px] font-mono text-zinc-500 space-y-0.5">
                    {msg.meta.model ? <div>model={msg.meta.model}</div> : null}
                    {msg.meta.mode ? <div>mode={msg.meta.mode}</div> : null}
                    {msg.meta.schema ? <div>schema={msg.meta.schema}</div> : null}
                    {msg.meta.latency_ms != null ? <div>latency_ms={String(msg.meta.latency_ms)}</div> : null}
                    <div>
                      receipt={msg.meta.has_receipt ? 'server' : 'none'} · watermark=
                      {msg.meta.has_watermark ? 'server' : 'none'}
                    </div>
                    {msg.meta.ok === false ? <div className="text-amber-500">ok=false</div> : null}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
          {(isProcessing || isIdleThinking) && (
            <div className="flex flex-col items-start">
              <div className="text-[10px] text-red-400 mb-1 uppercase tracking-widest font-bold flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-ping" />
                [EXTENSUS] {
                  isIdleThinking
                    ? "thinking..."
                    : [
                        "Thinking... What the fuck is up?",
                        "Thinking... You gonna sign in or we finna stare at eachother?",
                        "Thinking... Spit the beans or Spill the means!",
                        "Thinking... Fuck are we doing today?",
                        "Thinking... Ayo Bossman, line it up!",
                        "Thinking... Processing your shit..."
                      ][thinkingElapsed % 6]
                }
              </div>
              <div className="max-w-[85%] px-3.5 py-2.5 text-sm border border-red-900/80 bg-zinc-900/80 rounded-2xl rounded-bl-md text-red-200 shadow-[0_0_12px_rgba(239,68,68,0.2)]">
                <span className="inline-flex gap-1 items-center">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse [animation-delay:150ms]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse [animation-delay:300ms]" />
                  <span className="ml-2 text-zinc-400 font-mono text-[11px]">
                    {showOperatorChrome && memoryPulse?.ollama_quant
                      ? `${memoryPulse.ollama_quant} · `
                      : showOperatorChrome && memoryPulse?.ollama_ok
                        ? 'Ollama · '
                        : ''}
                    {thinkingElapsed}s
                  </span>
                </span>
              </div>
            </div>
          )}
          {pendingAction && (
            <div className="flex flex-col items-start">
              <div className="text-[10px] text-amber-500 mb-1 uppercase tracking-widest">
                [INTENT GATE] {formatChatTime(nowStamp(), clockZone)}
              </div>
              <div className="max-w-[92%] px-4 py-3 text-sm border border-amber-500 bg-amber-950/30 rounded-xl">
                <div className="font-bold text-amber-400 mb-2">
                  {pendingAction.type === 'execute' ? 'Will change something' : 'Talk or do?'}
                </div>
                <div className="text-amber-200/80 text-xs mb-2">“{pendingAction.query}”</div>
                {pendingAction.payload ? (
                  <pre className="text-[10px] text-amber-100/80 bg-black/40 rounded-md p-2 mb-3 overflow-x-auto whitespace-pre-wrap">
                    {JSON.stringify(pendingAction.payload, null, 2)}
                  </pre>
                ) : null}
                {pendingAction.bridge_ok === false ? (
                  <div className="text-[10px] text-amber-300/80 mb-3">Bridge looks down. APPROVE will still try.</div>
                ) : null}
                <div className="flex gap-3 flex-wrap">
                  {pendingAction.type === 'execute' ? (
                    <>
                      <button
                        onClick={() => handlePendingDecision('approve')}
                        className="px-4 py-2 bg-amber-500 text-black font-black text-xs tracking-widest uppercase hover:bg-amber-400 rounded-md"
                      >
                        APPROVE
                      </button>
                      <button
                        onClick={() => handlePendingDecision('cancel')}
                        className="px-4 py-2 border border-amber-500 text-amber-500 font-bold text-xs tracking-widest uppercase hover:bg-amber-900 rounded-md"
                      >
                        CANCEL
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handlePendingDecision('talk')}
                        className="px-4 py-2 border border-amber-500 text-amber-500 font-bold text-xs tracking-widest uppercase hover:bg-amber-900 rounded-md"
                      >
                        TALK
                      </button>
                      <button
                        onClick={() => handlePendingDecision('execute')}
                        className="px-4 py-2 bg-amber-500 text-black font-black text-xs tracking-widest uppercase hover:bg-amber-400 rounded-md"
                      >
                        EXECUTE
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Composer */}
        <form
          id="extensus-chat-form"
          onSubmit={handleSendMessage}
          className="extensus-composer p-3 border-t border-zinc-800 bg-black/80 space-y-2"
        >
          {attachments.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {attachments.map((item) => {
                const preview = item.type.startsWith('image/') ? URL.createObjectURL(item.file) : '';
                return (
                <span
                  key={item.id}
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border border-zinc-700 text-[10px] text-zinc-300"
                >
                  {preview ? (
                    <img src={preview} alt="" className="h-10 w-10 object-cover rounded" />
                  ) : null}
                  {item.name}
                  <button
                    type="button"
                    className="text-zinc-500 hover:text-white"
                    onClick={() => setAttachments((prev) => prev.filter((row) => row.id !== item.id))}
                    aria-label={`Remove ${item.name}`}
                  >
                    ×
                  </button>
                </span>
                );
              })}
            </div>
          ) : null}
          <div className="flex gap-2 items-center">
            <input
              ref={fileRef}
              id="extensus-add-file"
              type="file"
              multiple
              accept="image/*,video/*,.pdf,.txt,.md,.docx,.png,.jpg,.jpeg,.webp,.gif,.mp4,.mov"
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (!files.length) return;
                setAttachments((prev) => [
                  ...prev,
                  ...files.map((file) => ({
                    id: `${file.name}-${file.size}-${file.lastModified}`,
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    file,
                  })),
                ]);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              id="extensus-add-btn"
              data-testid="extensus-add-btn"
              title="Add photo, video, or file"
              onClick={() => fileRef.current?.click()}
              disabled={isProcessing || !tonySession.authenticated}
              className="w-10 h-10 shrink-0 rounded-full border border-zinc-600 text-xl leading-none text-zinc-200 hover:border-white hover:text-white disabled:opacity-40"
            >
              +
            </button>
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                tonySession.authenticated
                  ? tonySession.role === 'master'
                    ? 'Objective — EXTENSUS executes, then reports.'
                    : `Message EXTENSUS as ${tonySession.user_id}…`
                  : 'Message EXTENSUS (sign in once — master or guest)…'
              }
              className="flex-1 bg-zinc-900 border border-zinc-700 px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-600/60 rounded-xl"
              disabled={isProcessing}
              autoComplete="off"
              enterKeyHint="send"
            />
            {isProcessing ? (
              <button
                type="button"
                onClick={stopChat}
                className="px-5 py-2.5 bg-red-900 text-red-100 text-xs font-black tracking-widest uppercase rounded-xl hover:bg-red-800"
              >
                Stop {thinkingElapsed}s
              </button>
            ) : (
              <button
                type="submit"
                disabled={!input.trim() && attachments.length === 0}
                className="px-5 py-2.5 bg-white text-black text-xs font-black tracking-widest uppercase disabled:opacity-40 rounded-xl hover:bg-emerald-200"
              >
                Send
              </button>
            )}
          </div>
        </form>
      </div>

      {showPair && pairInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4">
          <div className="w-full max-w-md rounded-2xl border border-zinc-700 bg-zinc-900 p-6 text-white shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
              <div>
                <h2 className="text-xs font-black uppercase tracking-widest text-emerald-400">EXTENSUS NODE PAIRING CENTER</h2>
                <p className="text-[10px] text-zinc-400 mt-0.5">Pair secondary devices to your hardware mesh</p>
              </div>
              <button
                type="button"
                onClick={() => setShowPair(false)}
                className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-zinc-300"
              >
                Close
              </button>
            </div>

            <div className="space-y-3.5 text-xs">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-1">1-Click Master Pairing Link</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={pairInfo.pairing_url || ''}
                    className="min-w-0 flex-1 bg-black border border-zinc-800 rounded-lg px-3 py-2 text-zinc-200 font-mono text-[11px] truncate focus:outline-none focus:border-emerald-500"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (pairInfo.pairing_url) {
                        navigator.clipboard.writeText(pairInfo.pairing_url);
                        setCopyTokenStatus('link_copied');
                        setTimeout(() => setCopyTokenStatus(null), 2500);
                      }
                    }}
                    className="shrink-0 bg-emerald-500 hover:bg-emerald-400 text-black font-black uppercase text-[10px] tracking-wider px-3.5 py-2 rounded-lg transition-colors"
                  >
                    {copyTokenStatus === 'link_copied' ? 'Copied!' : 'Copy Link'}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-1">Pairing Token Code</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={pairInfo.pairing_token || ''}
                    className="min-w-0 flex-1 bg-black border border-zinc-800 rounded-lg px-3 py-2 text-emerald-400 font-mono text-[11px] truncate focus:outline-none focus:border-emerald-500"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (pairInfo.pairing_token) {
                        navigator.clipboard.writeText(pairInfo.pairing_token);
                        setCopyTokenStatus('token_copied');
                        setTimeout(() => setCopyTokenStatus(null), 2500);
                      }
                    }}
                    className="shrink-0 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 hover:text-white border border-zinc-700 font-black uppercase text-[10px] tracking-wider px-3.5 py-2 rounded-lg transition-colors"
                  >
                    {copyTokenStatus === 'token_copied' ? 'Copied!' : 'Copy Code'}
                  </button>
                </div>
              </div>

              <div className="bg-zinc-950 border border-zinc-800 p-3 rounded-xl text-[10px] text-zinc-400 space-y-1">
                <div className="font-bold text-white uppercase tracking-wider">How to Pair:</div>
                <div>1. Open the copied link on your iPhone, iPad, or laptop.</div>
                <div>2. Tap <span className="text-emerald-400 font-bold">Kappeesh</span> to authorize compute.</div>
                <div>3. The device immediately joins your active hardware mesh.</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAnalytics && analyticsData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4 overflow-y-auto">
          <div className="w-full max-w-3xl rounded-2xl border border-zinc-700/80 bg-zinc-900/95 p-6 text-white shadow-2xl space-y-6 my-auto max-h-[90vh] overflow-y-auto font-sans">
            <div className="flex flex-wrap items-center justify-between border-b border-zinc-800 pb-4 gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-black uppercase tracking-widest text-emerald-400">EXTENSUS ANALYTICS</h2>
                  <span className="text-[9px] font-mono font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    LIVE MESH MESH
                  </span>
                </div>
                <p className="text-[11px] text-zinc-400 mt-1">
                  Distributed hardware posture, real-time node count, and WebGPU compute telemetry
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={broadcastMeshPing}
                  className="text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 bg-cyan-950/60 hover:bg-cyan-900/60 text-cyan-300 border border-cyan-800 rounded-lg transition-colors flex items-center gap-1.5 shadow-[0_0_10px_rgba(6,182,212,0.15)]"
                  title="Broadcast ping signal across all connected swarm devices"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                  Ping Mesh
                </button>
                <button
                  type="button"
                  onClick={loadAnalytics}
                  className="text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-zinc-200 border border-zinc-700 transition-colors flex items-center gap-1.5"
                >
                  <svg className={`w-3 h-3 ${analyticsData.loading ? 'animate-spin text-emerald-400' : 'text-zinc-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAnalytics(false);
                    loadPairing();
                  }}
                  className="text-[10px] font-black uppercase tracking-wider px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 text-black rounded-lg transition-colors shadow-[0_0_12px_rgba(16,185,129,0.2)]"
                >
                  + Pair Node
                </button>
                <button
                  type="button"
                  onClick={() => setShowAnalytics(false)}
                  className="text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-zinc-300 transition-colors border border-zinc-700"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-zinc-950/80 border border-zinc-800 p-3.5 rounded-xl space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Total Mesh Nodes</span>
                <div className="text-2xl font-black text-white font-mono flex items-baseline gap-2">
                  {analyticsData.devices.length}
                  <span className="text-[10px] font-sans font-bold text-emerald-400 uppercase tracking-normal">Connected</span>
                </div>
                <p className="text-[9px] text-zinc-500 truncate">Registered in sovereign D1 ledger</p>
              </div>

              <div className="bg-zinc-950/80 border border-zinc-800 p-3.5 rounded-xl space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Active Workers</span>
                <div className="text-2xl font-black text-emerald-400 font-mono flex items-baseline gap-2">
                  {analyticsData.devices.filter((d: any) => d.accept_jobs !== false && d.label !== 'fallback').length}
                  <span className="text-[10px] font-sans font-bold text-zinc-400 uppercase tracking-normal">WebGPU</span>
                </div>
                <p className="text-[9px] text-zinc-500 truncate">Accepting & running infer jobs</p>
              </div>

              <div className="bg-zinc-950/80 border border-zinc-800 p-3.5 rounded-xl space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Swarm Memory</span>
                <div className="text-2xl font-black text-cyan-400 font-mono flex items-baseline gap-2">
                  {analyticsData.devices.reduce((acc: number, d: any) => {
                    const tier = String(d.loaded_tier || d.tier || '');
                    return acc + (tier === '8b' ? 8 : tier === '3b' ? 3 : tier === '1b' ? 1 : 0.5);
                  }, 0)}B
                  <span className="text-[10px] font-sans font-bold text-zinc-400 uppercase tracking-normal">VRAM</span>
                </div>
                <p className="text-[9px] text-zinc-500 truncate">Aggregated model capacity</p>
              </div>

              <div className="bg-zinc-950/80 border border-zinc-800 p-3.5 rounded-xl space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Sync Heartbeat</span>
                <div className="text-2xl font-black text-white font-mono flex items-baseline gap-2">
                  &lt;150ms
                  <span className="text-[10px] font-sans font-bold text-emerald-400 uppercase tracking-normal">Adaptive</span>
                </div>
                <p className="text-[9px] text-zinc-500 truncate">Last poll {analyticsData.lastUpdated || 'just now'}</p>
              </div>
            </div>

            <div className="bg-zinc-950/90 border border-zinc-800/80 p-4 rounded-xl space-y-3">
              <h3 className="text-xs font-black uppercase tracking-wider text-zinc-300 flex items-center justify-between">
                <span>Hardware Topology & Platform Distribution</span>
                <span className="text-[10px] text-zinc-500 font-mono font-normal">Auto-detected via Introspection</span>
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                <div className="bg-zinc-900/60 p-3 rounded-lg border border-zinc-800/60 space-y-2">
                  <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Operating Systems</div>
                  <div className="space-y-1.5 font-mono text-[11px]">
                    {['macos', 'ios', 'android', 'linux', 'windows', 'web'].map((osName) => {
                      const count = analyticsData.devices.filter((d: any) => String(d.os || d.id || '').toLowerCase().includes(osName)).length;
                      if (count === 0 && !['macos', 'ios'].includes(osName)) return null;
                      return (
                        <div key={osName} className="flex items-center justify-between text-zinc-300">
                          <span className="uppercase text-[10px] font-sans text-zinc-400">{osName}</span>
                          <span className="font-bold text-emerald-400">{count} node{count !== 1 ? 's' : ''}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-zinc-900/60 p-3 rounded-lg border border-zinc-800/60 space-y-2">
                  <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Loaded Compute Tiers</div>
                  <div className="space-y-1.5 font-mono text-[11px]">
                    {['8b', '3b', '1b', '0.5b'].map((tierName) => {
                      const count = analyticsData.devices.filter((d: any) => String(d.loaded_tier || d.tier || '').toLowerCase() === tierName).length;
                      return (
                        <div key={tierName} className="flex items-center justify-between text-zinc-300">
                          <span className="uppercase text-[10px] font-sans text-zinc-400">{tierName} MLC</span>
                          <span className="font-bold text-cyan-400">{count} active</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-zinc-900/60 p-3 rounded-lg border border-zinc-800/60 space-y-2">
                  <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Swarm Roles</div>
                  <div className="space-y-1.5 font-mono text-[11px]">
                    <div className="flex items-center justify-between text-zinc-300">
                      <span className="uppercase text-[10px] font-sans text-zinc-400">Master Bossman</span>
                      <span className="font-bold text-emerald-400">1 (Primary)</span>
                    </div>
                    <div className="flex items-center justify-between text-zinc-300">
                      <span className="uppercase text-[10px] font-sans text-zinc-400">Active Workers</span>
                      <span className="font-bold text-emerald-400">
                        {analyticsData.devices.filter((d: any) => d.accept_jobs !== false).length}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-zinc-300">
                      <span className="uppercase text-[10px] font-sans text-zinc-400">Chat / Fallbacks</span>
                      <span className="font-bold text-zinc-400">
                        {analyticsData.devices.filter((d: any) => d.accept_jobs === false || d.label === 'fallback').length}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-black uppercase tracking-wider text-zinc-300">Connected Device Roster</h3>
                <span className="text-[10px] text-zinc-500 font-mono">Total {analyticsData.devices.length} registered</span>
              </div>

              {analyticsData.devices.length === 0 ? (
                <div className="bg-zinc-950 border border-zinc-800 p-6 rounded-xl text-center space-y-3">
                  <p className="text-xs text-zinc-400">No secondary devices currently attached to your hardware mesh.</p>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAnalytics(false);
                      loadPairing();
                    }}
                    className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black font-black uppercase text-[10px] tracking-wider rounded-lg transition-colors"
                  >
                    Pair iPhone / iPad / Secondary Node
                  </button>
                </div>
              ) : (
                <div className="border border-zinc-800 rounded-xl overflow-hidden bg-zinc-950/80">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-[11px]">
                      <thead className="bg-zinc-900/90 text-zinc-400 uppercase text-[9px] font-bold tracking-wider border-b border-zinc-800">
                        <tr>
                          <th className="py-2.5 px-3">Node ID / Name</th>
                          <th className="py-2.5 px-3">OS & Runtime</th>
                          <th className="py-2.5 px-3">Swarm Role</th>
                          <th className="py-2.5 px-3">Tier</th>
                          <th className="py-2.5 px-3 text-right">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-800/60 font-mono text-[11px]">
                        {analyticsData.devices.map((device: any, idx: number) => {
                          const isBusy = device.busy;
                          const isWorker = device.accept_jobs !== false;
                          const label = device.label || device.id || `node-${idx + 1}`;
                          const os = device.os || (String(device.id).includes('ios') ? 'iOS' : String(device.id).includes('android') ? 'Android' : 'macOS');
                          const tier = device.loaded_tier || device.tier || '8b';

                          return (
                            <tr key={device.id || idx} className="hover:bg-zinc-900/50 transition-colors">
                              <td className="py-2.5 px-3 text-white font-bold font-sans flex items-center gap-2 truncate max-w-[180px]">
                                <span className={`w-2 h-2 rounded-full ${isBusy ? 'bg-amber-400 animate-ping' : isWorker ? 'bg-emerald-400' : 'bg-zinc-500'}`} />
                                <span className="truncate" title={label}>{label}</span>
                              </td>
                              <td className="py-2.5 px-3 text-zinc-300">
                                <span className="uppercase text-[10px] text-zinc-400">{os}</span> / <span className="text-[10px] text-zinc-400">{device.runtime || 'webllm'}</span>
                              </td>
                              <td className="py-2.5 px-3 text-emerald-400 text-[10px] uppercase font-sans font-semibold">
                                {device.roles?.join(', ') || (isWorker ? 'reasoner, classifier' : 'chat-only')}
                              </td>
                              <td className="py-2.5 px-3 text-cyan-400 uppercase text-[10px]">
                                {tier}
                              </td>
                              <td className="py-2.5 px-3 text-right">
                                {isBusy ? (
                                  <span className="inline-block px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/40">
                                    BUSY
                                  </span>
                                ) : isWorker ? (
                                  <span className="inline-block px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                                    ACTIVE
                                  </span>
                                ) : (
                                  <span className="inline-block px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-zinc-800 text-zinc-400 border border-zinc-700">
                                    IDLE
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-zinc-950/60 border border-zinc-800/80 p-3 rounded-xl flex items-center justify-between text-[10px] text-zinc-500">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span>Extensus Sovereign Telemetry • Private D1 Ledger Sync</span>
              </div>
              <div>
                Last Polled: <span className="font-mono text-zinc-400">{analyticsData.lastUpdated || 'Just now'}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default CorpusAwareChat;
